import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Clock, Plus, Trash2, Download, Play, Square, 
  LayoutDashboard, Monitor, ArrowLeft, CheckCircle2, Lock, Coffee, 
  PlayCircle, AlertTriangle, BarChart3, CalendarDays, TrendingUp, 
  Palmtree, Plane, XCircle, Edit, Save, X, KeyRound, 
  UserCog, Database, Cloud, RefreshCw, 
  Settings as IconSettings, 
  History as IconHistory,
  FileText,
  Check,
  CornerDownLeft,
  AlertOctagon,
  RotateCcw,
  Delete,
  UploadCloud,
  Folder,
  Thermometer,
  Wallet,
  Mail
} from 'lucide-react';

// Firebase Imports
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, onSnapshot, deleteDoc, query, where } from 'firebase/firestore';

// ==========================================================================================
// 🔧 KONFIGURATION
// ==========================================================================================

const myFirebaseConfig = {
  apiKey: "AIzaSyB3wvOrdACjWAWiixg2-0zbpxw0zTxMNzI",
  authDomain: "zeiterfassung-gude.firebaseapp.com",
  projectId: "zeiterfassung-gude",
  storageBucket: "zeiterfassung-gude.firebasestorage.app",
  messagingSenderId: "628618093556",
  appId: "1:628618093556:web:872e9e66e3a93cff998fa1",
  measurementId: "G-3L02VGC7EE"
};

// ==========================================================================================

// ==========================================================================================
// 🎌 Gesetzliche Feiertage (nach Bundesland) – berechnet inkl. beweglicher Feiertage
// ==========================================================================================
const BUNDESLAND_EXTRA_FEIERTAGE = {
  BW: ['heiligeDreiKoenige', 'fronleichnam', 'allerheiligen'],
  BY: ['heiligeDreiKoenige', 'fronleichnam', 'allerheiligen', 'mariaHimmelfahrt'],
  BE: ['frauentag'],
  BB: ['reformationstag'],
  HB: ['reformationstag'],
  HH: ['reformationstag'],
  HE: ['fronleichnam'],
  MV: ['frauentag', 'reformationstag'],
  NI: ['reformationstag'],
  NW: ['fronleichnam', 'allerheiligen'],
  RP: ['fronleichnam', 'allerheiligen'],
  SL: ['fronleichnam', 'mariaHimmelfahrt', 'allerheiligen'],
  SN: ['reformationstag'],
  ST: ['heiligeDreiKoenige', 'reformationstag'],
  SH: ['reformationstag'],
  TH: ['weltkindertag', 'reformationstag']
};
const _feiertagCache = {};
function feiertageFuerJahr(year, land) {
  const key = (land || 'HE') + '_' + year;
  if (_feiertagCache[key]) return _feiertagCache[key];
  // Ostersonntag nach Gauss'scher Osterformel
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const mm = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * mm + 114) / 31);
  const day = ((h + l - 7 * mm + 114) % 31) + 1;
  const ostern = new Date(Date.UTC(year, month - 1, day));
  const isoUTC = dt => dt.toISOString().slice(0, 10);
  const plus = n => { const x = new Date(ostern); x.setUTCDate(x.getUTCDate() + n); return isoUTC(x); };
  const fest = (m2, d2) => year + '-' + String(m2).padStart(2, '0') + '-' + String(d2).padStart(2, '0');
  const extra = BUNDESLAND_EXTRA_FEIERTAGE[land || 'HE'] || [];
  const has = name => extra.indexOf(name) !== -1;
  const tage = {};
  tage[fest(1, 1)] = 'Neujahr';
  if (has('heiligeDreiKoenige')) tage[fest(1, 6)] = 'Heilige Drei Könige';
  if (has('frauentag')) tage[fest(3, 8)] = 'Frauentag';
  tage[plus(-2)] = 'Karfreitag';
  tage[plus(1)] = 'Ostermontag';
  tage[fest(5, 1)] = 'Tag der Arbeit';
  tage[plus(39)] = 'Christi Himmelfahrt';
  tage[plus(50)] = 'Pfingstmontag';
  if (has('fronleichnam')) tage[plus(60)] = 'Fronleichnam';
  if (has('mariaHimmelfahrt')) tage[fest(8, 15)] = 'Mariä Himmelfahrt';
  if (has('weltkindertag')) tage[fest(9, 20)] = 'Weltkindertag';
  tage[fest(10, 3)] = 'Tag der Deutschen Einheit';
  if (has('reformationstag')) tage[fest(10, 31)] = 'Reformationstag';
  if (has('allerheiligen')) tage[fest(11, 1)] = 'Allerheiligen';
  tage[fest(12, 25)] = '1. Weihnachtstag';
  tage[fest(12, 26)] = '2. Weihnachtstag';
  _feiertagCache[key] = tage;
  return tage;
}
function feiertagName(dateIso, land) {
  if (!dateIso || dateIso.length < 10) return null;
  const y = parseInt(dateIso.slice(0, 4), 10);
  if (isNaN(y)) return null;
  return feiertageFuerJahr(y, land)[dateIso] || null;
}

export default function App() {
  
  const [appMode, setAppMode] = useState('terminal');

  // Dynamische Styles einfügen
  useEffect(() => {
    if (!document.getElementById('tailwind-cdn')) {
      const script = document.createElement('script');
      script.id = 'tailwind-cdn';
      script.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(script);
    }
    if (!document.getElementById('kiosk-styles')) {
      const style = document.createElement('style');
      style.id = 'kiosk-styles';
      style.innerHTML = `
        html, body {
          background-color: #f8fafc;
          -webkit-user-select: none; user-select: none;
        }
        .kiosk-mode {
          overscroll-behavior: none; touch-action: pan-x pan-y;
          height: 100%; overflow: hidden;
        }
        .overflow-y-auto { -webkit-overflow-scrolling: touch; }
        .allow-scroll { overflow-y: auto !important; -webkit-overflow-scrolling: touch; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Intelligente Scroll-Sperre je nach App-Modus
  useEffect(() => {
    if (appMode === 'terminal') {
      document.documentElement.classList.add('kiosk-mode');
      document.body.classList.add('kiosk-mode');
    } else {
      document.documentElement.classList.remove('kiosk-mode');
      document.body.classList.remove('kiosk-mode');
    }
  }, [appMode]);

  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null); 
  const [user, setUser] = useState(null);
  const [isCloudConfigured, setIsCloudConfigured] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [employees, setEmployees] = useState([]); 
  const [loading, setLoading] = useState(true);

  // Notfall-Timeout gegen unendliches Laden
  useEffect(() => {
      const safetyTimer = setTimeout(() => {
          setLoading(false);
      }, 3500); 
      return () => clearTimeout(safetyTimer);
  }, []);

  useEffect(() => {
    if (myFirebaseConfig.apiKey) {
      try {
        const app = initializeApp(myFirebaseConfig);
        const authInstance = getAuth(app);
        const firestore = getFirestore(app);
        setDb(firestore);
        setAuth(authInstance);
        setIsCloudConfigured(true);
        
        setPersistence(authInstance, browserLocalPersistence)
          .then(() => signInAnonymously(authInstance))
          .catch((error) => { 
              console.error("Auth Init Error:", error); 
              setConnectionError("Anmelde-Fehler: " + error.message); 
              setLoading(false); 
          });
          
        return onAuthStateChanged(authInstance, (currentUser) => {
            setUser(currentUser);
            if(currentUser) setConnectionError(null);
            else signInAnonymously(authInstance).catch(e => {
                setConnectionError(e.message);
                setLoading(false); 
            });
        });
      } catch (e) { 
          console.error("Firebase Init Error:", e); 
          setConnectionError("Init Fehler: " + e.message); 
          setLoading(false); 
      }
    } else {
        setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isCloudConfigured && db && user) {
        const empRef = collection(db, 'employees');
        const unsubEmp = onSnapshot(empRef, (snapshot) => {
            const data = snapshot.docs.map(doc => {
                const d = { id: doc.id, ...doc.data() };
                if (d.logs && Array.isArray(d.logs)) {
                    d.logs.sort((a,b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
                } else {
                    d.logs = [];
                }
                if (!d.requests) d.requests = [];
                if (!d.vacationBookings) d.vacationBookings = [];
                if (!d.sickBookings) d.sickBookings = [];
                return d;
            });
            data.sort((a,b) => a.name.localeCompare(b.name));
            setEmployees(data);
            setLoading(false);
            setConnectionError(null);
        }, (error) => {
            if (error.code === 'permission-denied') setConnectionError("Keine Berechtigung (Datenbank-Regeln prüfen!)");
            else setConnectionError(error.message);
        });
        const settingsRef = doc(db, 'config', 'main_settings');
        const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists()) setAdminSettings(docSnap.data());
            else setDoc(settingsRef, { adminPin: '0000' }).catch(e => console.error("Settings write err", e));
        });
        return () => { unsubEmp(); unsubSettings(); };
    } else if (!isCloudConfigured) {
        try {
            const saved = localStorage.getItem('time-tracker-data');
            if (saved) setEmployees(JSON.parse(saved));
            const savedSettings = localStorage.getItem('time-tracker-settings');
            if(savedSettings) setAdminSettings(JSON.parse(savedSettings));
        } catch(e) { console.error(e); }
        setLoading(false);
    }
  }, [isCloudConfigured, db, user]);

  const forceReconnect = async () => {
      if(auth) {
          try { setLoading(true); await signInAnonymously(auth); setConnectionError(null); window.location.reload(); } 
          catch(e) { alert("Konnte nicht neu verbinden: " + e.message); setConnectionError(e.message); } 
          finally { setLoading(false); }
      }
  };

  const [adminSettings, setAdminSettings] = useState({ adminPin: '0000', bundesland: 'HE', abrechnungStartTag: 21 });
  const bundesland = adminSettings.bundesland || 'HE';

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [vacationForm, setVacationForm] = useState({ start: '', end: '' });
  const [sickForm, setSickForm] = useState({ start: '', end: '' });
  const [timeRange, setTimeRange] = useState('week');
  const [customDateRange, setCustomDateRange] = useState({ start: new Date().toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] });
  const [terminalMessage, setTerminalMessage] = useState(null);
  
  const [terminalPinInput, setTerminalPinInput] = useState('');
  const [terminalError, setTerminalError] = useState('');
  const [pendingAuthEmpId, setPendingAuthEmpId] = useState(null);
  
  const [staleEntry, setStaleEntry] = useState(null); 
  const [staleEndTime, setStaleEndTime] = useState('16:00');

  const [deleteConfirmState, setDeleteConfirmState] = useState({ isOpen: false, empId: null });
  const [editModal, setEditModal] = useState({ isOpen: false, mode: 'add', empId: null, data: { date: '', startTime: '', endTime: '', pauseMinutes: 0 } });
  const [showPinModal, setShowPinModal] = useState(false); 
  const [showUserPinModal, setShowUserPinModal] = useState(false); 
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false); 
  const [newPinInput, setNewPinInput] = useState('');
  const [showEmployeeSettingsModal, setShowEmployeeSettingsModal] = useState(false);
  const [employeeEditForm, setEmployeeEditForm] = useState({name: '', type: 'fulltime', hours: 40, vacation: 30, userPin: '', email: '', overtimeAdjust: 0});
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false); 
  const [requestForm, setRequestForm] = useState({ date: '', time: '', type: 'start', reason: '', minutes: '' });

  const [payslipFile, setPayslipFile] = useState(null);
  const [payslipMonth, setPayslipMonth] = useState('');
  const [activePayslips, setActivePayslips] = useState([]);
  const fileInputRefDocs = useRef(null);

  const [showUserTimesheet, setShowUserTimesheet] = useState(false);
  const [showPayslipsUserModal, setShowPayslipsUserModal] = useState(false);

  useEffect(() => {
      if (!selectedEmployeeId) {
          setShowUserTimesheet(false);
          setShowPayslipsUserModal(false);
          setActivePayslips([]);
          setPayslipFile(null);
          setPayslipMonth('');
      }
  }, [selectedEmployeeId]);

  useEffect(() => {
      if (!selectedEmployeeId || !db) {
          setActivePayslips([]);
          return;
      }
      const q = query(collection(db, 'payslips'), where('empId', '==', selectedEmployeeId));
      const unsub = onSnapshot(q, (snap) => {
          const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          data.sort((a,b) => b.month.localeCompare(a.month)); 
          setActivePayslips(data);
      }, (error) => {
          console.error("Dokumente Fehler:", error);
      });
      return () => unsub();
  }, [selectedEmployeeId, db]);

  const fileInputRef = useRef(null);

  const generateId = () => Math.random().toString(36).substr(2, 9);
  
  // ROBUSTER FIX: Datums- und Uhrzeitverarbeitung kugelsicher machen
  const formatTime = (isoString) => { 
      if (!isoString) return '-'; 
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  };
  const formatDate = (isoString) => { 
      if (!isoString) return '-'; 
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return String(d.getDate()).padStart(2, '0') + '.' + String(d.getMonth() + 1).padStart(2, '0') + '.' + d.getFullYear(); 
  };
  const formatDateGerman = (dateString) => { 
      if(!dateString) return '-'; 
      const parts = dateString.split('-'); 
      if (parts.length !== 3) return dateString;
      return `${parts[2]}.${parts[1]}.${parts[0]}`; 
  };
  const calculateMinutesDiff = (start, end) => { 
      const diff = new Date(end).getTime() - new Date(start).getTime(); 
      return Math.floor(diff / 1000 / 60); 
  };
  const formatDuration = (minutes) => { 
      const h = Math.floor((minutes || 0) / 60); 
      const m = (minutes || 0) % 60; 
      return `${h}Std ${m}Min`; 
  };
  const formatDecimalDuration = (minutes) => { 
      if (minutes === null || minutes === undefined || isNaN(minutes)) return '0.0 Std'; 
      return (minutes / 60).toFixed(1) + ' Std'; 
  };
  const countWeekdays = (startDate, endDate) => { 
      let count = 0; 
      const curDate = new Date(startDate); 
      const stopDate = new Date(endDate); 
      while (curDate <= stopDate) { 
          const dayOfWeek = curDate.getDay(); 
          if (dayOfWeek !== 0 && dayOfWeek !== 6) count++; 
          curDate.setDate(curDate.getDate() + 1); 
      } 
      return count; 
  };
  const isTodayVacation = (bookings) => { 
      if(!bookings || !Array.isArray(bookings)) return false; 
      const today = new Date().toISOString().split('T')[0]; 
      return bookings.some(b => today >= b.startDate && today <= b.endDate); 
  };
  
  // ROBUSTER FIX: Extrahiert das Datum extrem zuverlässig
  const getISODatePart = (isoString) => { 
      if (!isoString) return '';
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '';
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  };
  // ROBUSTER FIX: Extrahiert die Uhrzeit extrem zuverlässig ("HH:MM")
  const getISOTimePart = (isoString) => { 
      if (!isoString) return '';
      const d = new Date(isoString); 
      if (isNaN(d.getTime())) return '';
      return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0'); 
  };
  
  const getEmploymentLabel = (type) => { switch(type) { case 'fulltime': return 'Vollzeit'; case 'parttime': return 'Teilzeit'; case 'minijob': return 'Aushilfe'; default: return type; } };

  const formatMonthName = (yyyyMm) => {
      if(!yyyyMm) return '';
      if (yyyyMm.includes('-')) {
          const parts = yyyyMm.split('-');
          if (parts.length === 2) {
              const [y, m] = parts;
              const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
              const monthIndex = parseInt(m, 10) - 1;
              if (!isNaN(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
                  return `${months[monthIndex]} ${y}`;
              }
          }
      }
      return yyyyMm;
  };

  const isDateToday = (isoString) => {
      if (!isoString) return false;
      const today = new Date().toDateString();
      const check = new Date(isoString).toDateString();
      return today === check;
  };

  const filterLogsByRange = (logs, range) => {
    if (!Array.isArray(logs)) return []; 
    const now = new Date();
    now.setHours(0,0,0,0);
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const startOfWeek = new Date(now); startOfWeek.setDate(diff);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const endOfToday = new Date(now); endOfToday.setHours(23,59,59,999);

    return logs.filter(log => {
      const logDate = new Date(log.startTime);
      if (range === 'custom') {
          if (!customDateRange.start || !customDateRange.end) return true;
          const startCustom = new Date(customDateRange.start); startCustom.setHours(0,0,0,0);
          const endCustom = new Date(customDateRange.end); endCustom.setHours(23,59,59,999);
          return logDate >= startCustom && logDate <= endCustom;
      }
      switch (range) {
        case 'today': return logDate >= now && logDate <= endOfToday;
        case 'week': return logDate >= startOfWeek;
        case 'month': return logDate >= startOfMonth;
        case 'year': return logDate >= startOfYear;
        case 'all': return true;
        default: return true;
      }
    });
  };

  const getRangeLabel = () => {
      const now = new Date();
      if (timeRange === 'today') return formatDateGerman(now.toISOString().split('T')[0]);
      if (timeRange === 'week') {
          const day = now.getDay();
          const diff = now.getDate() - day + (day === 0 ? -6 : 1);
          const startOfWeek = new Date(now);
          startOfWeek.setDate(diff);
          return `Woche vom ${formatDateGerman(startOfWeek.toISOString().split('T')[0])}`;
      }
      if (timeRange === 'month') return `Monat ${now.getMonth() + 1}/${now.getFullYear()}`;
      if (timeRange === 'year') return `Jahr ${now.getFullYear()}`;
      if (timeRange === 'custom') return `${formatDateGerman(customDateRange.start)} - ${formatDateGerman(customDateRange.end)}`;
      return 'Gesamt';
  };

  // ROBUSTER FIX: Diese Funktion schützt die Datenbank zu 100% vor "undefined" oder kaputten Objekten
  const cleanForFirestore = (obj) => {
      if (obj === undefined) return null;
      if (obj === null || typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.map(item => cleanForFirestore(item));
      
      const newObj = {};
      for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
              newObj[key] = cleanForFirestore(obj[key]);
          }
      }
      return newObj;
  };

  const dashboardStats = useMemo(() => {
     let totalMinutes = 0; 
     let totalActiveDays = 0; 
     const uniqueDaysSet = new Set();
     employees.forEach(emp => {
       const filtered = filterLogsByRange(emp.logs || [], timeRange);
       filtered.forEach(log => {
         if (log.durationMinutes) { 
             totalMinutes += log.durationMinutes; 
             uniqueDaysSet.add(new Date(log.startTime).toDateString() + emp.id); 
         }
       });
     });
     totalActiveDays = uniqueDaysSet.size;
     const avgMinutesPerDay = totalActiveDays > 0 ? Math.round(totalMinutes / totalActiveDays) : 0;
     const pendingRequests = employees.reduce((acc, emp) => {
         const reqs = emp.requests || [];
         return acc + reqs.filter(r => r.status === 'pending').length;
     }, 0);
     return {
       totalHours: totalMinutes / 60,
       avgHoursPerDay: avgMinutesPerDay / 60,
       activeEmployeeCount: employees.filter(e => e.status !== 'idle' && !isTodayVacation(e.vacationBookings)).length,
       vacationCount: employees.filter(e => isTodayVacation(e.vacationBookings)).length,
       pendingRequests: pendingRequests
     };
  }, [employees, timeRange, customDateRange]);

  const handleDbError = (e) => {
      console.error("DB Error:", e);
      let msg = e.message;
      if(e.code === 'permission-denied') msg = "Zugriff verweigert!";
      if(e.code === 'unavailable') msg = "Keine Internetverbindung.";
      alert(`Fehler: ${msg}`);
      setConnectionError(msg);
  };

  const saveEmployee = async (employeeData) => {
      const cleanData = cleanForFirestore(employeeData);
      setEmployees(prev => {
          const exists = prev.find(e => e.id === cleanData.id);
          return exists ? prev.map(e => e.id === cleanData.id ? cleanData : e) : [...prev, cleanData];
      });
      if (isCloudConfigured && db) {
          try { await setDoc(doc(db, 'employees', cleanData.id), cleanData); } catch (e) { handleDbError(e); }
      }
  };

  const removeEmployee = async (empId) => {
      setEmployees(prev => prev.filter(e => e.id !== empId));
      if (isCloudConfigured && db) { try { await deleteDoc(doc(db, 'employees', empId)); } catch (e) { handleDbError(e); } }
  };

  const updateSettings = async (newSettings) => {
      setAdminSettings(newSettings);
      if (isCloudConfigured && db) { try { await setDoc(doc(db, 'config', 'main_settings'), newSettings); } catch (e) { handleDbError(e); } }
  };

  const handleAdminAuth = () => {
      const inputStr = String(pinInput).trim();
      const adminStr = String(adminSettings.adminPin).trim();
      
      if (inputStr === adminStr || (inputStr === '0000' && employees.length === 0)) { 
          setAppMode('admin'); 
          setShowPinModal(false); 
          setPinInput(''); 
          setPinError(false); 
      } 
      else { 
          setPinError(true); 
          setPinInput(''); 
      }
  };

  const saveSettingsHandler = () => { updateSettings({ ...adminSettings, adminPin: newPinInput }); setShowSettingsModal(false); };

  const addEmployee = (e) => {
    e.preventDefault(); if (!newEmployeeName.trim()) return;
    const newEmp = { id: generateId(), name: newEmployeeName, status: 'idle', logs: [], requests: [], vacationEntitlement: 30, vacationBookings: [], sickBookings: [], employmentType: 'fulltime', weeklyHours: 40, userPin: null, email: '', overtimeAdjust: 0 };
    saveEmployee(newEmp); setNewEmployeeName('');
  };

  const requestDeleteEmployee = (id) => { setDeleteConfirmState({ isOpen: true, empId: id }); };

  const confirmDeleteEmployee = () => {
    if (deleteConfirmState.empId) { removeEmployee(deleteConfirmState.empId); if (selectedEmployeeId === deleteConfirmState.empId) setSelectedEmployeeId(null); }
    setDeleteConfirmState({ isOpen: false, empId: null });
  };

  const openEmployeeSettings = (emp) => {
      setEmployeeEditForm({ name: emp.name, type: emp.employmentType, hours: emp.weeklyHours, vacation: emp.vacationEntitlement, userPin: emp.userPin || '', email: emp.email || '', overtimeAdjust: emp.overtimeAdjust || 0 });
      setShowEmployeeSettingsModal(true);
  };

  const saveEmployeeSettings = () => {
      if(!selectedEmployeeId) return;
      const emp = employees.find(e => e.id === selectedEmployeeId); if (!emp) return;
      const updatedEmp = { ...emp, name: employeeEditForm.name, employmentType: employeeEditForm.type, weeklyHours: parseFloat(employeeEditForm.hours) || 0, vacationEntitlement: parseFloat(employeeEditForm.vacation) || 0, userPin: employeeEditForm.userPin || null, email: (employeeEditForm.email || '').trim(), overtimeAdjust: parseFloat(employeeEditForm.overtimeAdjust) || 0 };
      saveEmployee(updatedEmp); setShowEmployeeSettingsModal(false);
  };

  const checkAndSetEmployee = (emp) => {
      if (emp.status === 'working' || emp.status === 'break') {
          const currentLog = (emp.logs || [])[0];
          if (currentLog && !isDateToday(currentLog.startTime)) {
              setStaleEntry({ empId: emp.id, date: getISODatePart(currentLog.startTime), logId: currentLog.id });
              return; 
          }
      }
      setSelectedEmployeeId(emp.id);
  };

  const handleFixStaleEntry = () => {
      if (!staleEntry) return;
      const emp = employees.find(e => e.id === staleEntry.empId);
      if (!emp) return;
      
      const fullEndTime = new Date(`${staleEntry.date}T${staleEndTime}`).toISOString();
      let updatedLogs = [...(emp.logs || [])];
      
      updatedLogs = updatedLogs.map(l => {
          if (l.id === staleEntry.logId) {
              const totalDuration = calculateMinutesDiff(l.startTime, fullEndTime);
              let pause = l.totalPauseMinutes || 0;
              return { ...l, endTime: fullEndTime, durationMinutes: Math.max(0, totalDuration - pause), lastPauseStart: null };
          }
          return l;
      });
      
      saveEmployee({ ...emp, logs: updatedLogs, status: 'idle', currentEntryId: null });
      setStaleEntry(null);
      alert("Zeit nachgetragen. Bitte jetzt normal anmelden.");
  };

  const handleUserPinSubmit = () => {
      const emp = employees.find(e => e.id === pendingAuthEmpId);
      const inputStr = String(pinInput).trim();
      
      if (emp && String(emp.userPin).trim() === inputStr) {
          setShowUserPinModal(false);
          setPinInput('');
          setPendingAuthEmpId(null);
          checkAndSetEmployee(emp);
      } else {
          setPinError(true);
          setPinInput('');
      }
  };
  
  const handleForceReset = () => {
      if(!selectedEmployeeId) return;
      if(window.confirm("ACHTUNG: Dies setzt den Status des Mitarbeiters hart auf 'Abwesend' zurück. Offene Einträge werden geschlossen (mit aktueller Zeit). Nur im Notfall verwenden!")) {
          const emp = employees.find(e => e.id === selectedEmployeeId);
          if (!emp) return;
          
          let updatedLogs = [...(emp.logs || [])];
          if(emp.currentEntryId) {
              const now = new Date().toISOString();
              updatedLogs = updatedLogs.map(l => {
                  if(l.id === emp.currentEntryId) {
                      return { ...l, endTime: now, lastPauseStart: null }; 
                  }
                  return l;
              });
          }
          
          saveEmployee({ ...emp, status: 'idle', currentEntryId: null, logs: updatedLogs });
          alert("Status wurde zurückgesetzt.");
      }
  };

  const handleTerminalDigitClick = (digit) => {
      if (terminalPinInput.length < 4) {
          setTerminalPinInput(prev => prev + digit);
      }
  };

  const handleTerminalBackspace = () => {
      setTerminalPinInput(prev => prev.slice(0, -1));
  };
  
  const handleTerminalLogin = async () => {
      if (!terminalPinInput) return;
      const inputStr = String(terminalPinInput).trim();
      const emp = employees.find(e => String(e.userPin).trim() === inputStr);
      
      if (emp) {
          checkAndSetEmployee(emp);
          setTerminalPinInput('');
          setTerminalError('');
      } else {
          setTerminalError("PIN unbekannt.");
          setTerminalPinInput('');
          setTimeout(() => setTerminalError(''), 2000);
      }
  };

  const handlePayslipFileChange = (e) => {
      const file = e.target.files ? e.target.files[0] : null;
      if (!file) return;
      if (file.type !== 'application/pdf') {
          alert('Bitte nur PDF-Dateien hochladen.');
          e.target.value = '';
          return;
      }
      if (file.size > 800 * 1024) { 
          alert('Die Datei ist zu groß (Maximal 800 KB). Bitte verkleinere das PDF.');
          e.target.value = '';
          return;
      }
      setPayslipFile(file);
  };

  // ---------- E-Mail-Versand der Abrechnungen (über die Poststelle auf dem Server) ----------
  // Die Poststelle bekommt nur die Kennung der Abrechnung und liest Adresse + PDF selbst aus der Datenbank.
  const versendeAbrechnungMail = async (payslipId) => {
      try {
          const r = await fetch('/api/abrechnung-mail', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ payslipId })
          });
          const j = await r.json().catch(() => ({}));
          return r.ok ? { ok: true, an: j.an } : { ok: false, fehler: j.fehler || ('HTTP ' + r.status) };
      } catch (e) {
          return { ok: false, fehler: e.message };
      }
  };

  const sendPayslipMail = async (p) => {
      const emp = employees.find(e => e.id === p.empId);
      if (!emp || !emp.email) { alert('Für diesen Mitarbeiter ist keine E-Mail-Adresse hinterlegt (Stammdaten).'); return; }
      if (!window.confirm(`Abrechnung ${formatMonthName(p.month)} per E-Mail an ${emp.email} senden?`)) return;
      const erg = await versendeAbrechnungMail(p.id);
      if (erg.ok) alert(`✉️ E-Mail an ${emp.email} wurde verschickt.`);
      else alert('⚠️ E-Mail konnte nicht gesendet werden: ' + erg.fehler);
  };

  const uploadPayslip = () => {
      if (!payslipFile || !payslipMonth || !selectedEmployeeId || !db) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
          const base64Data = e.target ? e.target.result : null;
          const newId = generateId();
          const payslipDoc = {
              empId: selectedEmployeeId,
              month: payslipMonth,
              fileName: payslipFile.name,
              fileData: base64Data,
              createdAt: new Date().toISOString()
          };

          try {
              await setDoc(doc(db, 'payslips', newId), payslipDoc);
              // Automatischer E-Mail-Versand: die Poststelle liest Abrechnung + Adresse selbst aus der Datenbank
              const empMail = employees.find(e => e.id === selectedEmployeeId);
              let mailInfo = '';
              if (empMail && empMail.email) {
                  const erg = await versendeAbrechnungMail(newId);
                  mailInfo = erg.ok ? `\n\n✉️ E-Mail an ${empMail.email} wurde verschickt.` : `\n\n⚠️ E-Mail konnte nicht gesendet werden: ${erg.fehler}`;
              } else {
                  mailInfo = '\n\n(Keine E-Mail-Adresse hinterlegt – daher kein Versand.)';
              }
              setPayslipFile(null);
              setPayslipMonth('');
              if (fileInputRefDocs.current) fileInputRefDocs.current.value = '';
              alert("Lohnabrechnung erfolgreich hochgeladen!" + mailInfo);
          } catch (err) {
              alert("Fehler beim Hochladen: " + err.message);
          }
      };
      reader.readAsDataURL(payslipFile);
  };

  const deletePayslip = async (id) => {
      if(!window.confirm("Abrechnung wirklich unwiderruflich löschen?")) return;
      try {
          await deleteDoc(doc(db, 'payslips', id));
      } catch (err) {
          alert("Fehler: " + err.message);
      }
  };

  const downloadBase64PDF = (base64Data, fileName) => {
      const link = document.createElement('a');
      link.href = base64Data;
      link.download = fileName;
      link.click();
  };

  const bookVacation = (empId) => {
      if (!vacationForm.start || !vacationForm.end) return; if (vacationForm.end < vacationForm.start) { alert("Fehler im Datum"); return; }
      const days = countWeekdays(vacationForm.start, vacationForm.end);
      const emp = employees.find(e => e.id === empId); if (!emp) return;
      const newBooking = { id: generateId(), startDate: vacationForm.start, endDate: vacationForm.end, daysUsed: days };
      saveEmployee({ ...emp, vacationBookings: [...emp.vacationBookings, newBooking].sort((a,b) => a.startDate.localeCompare(b.startDate)) });
      setVacationForm({ start: '', end: '' });
  };

  const deleteVacation = (empId, bookingId) => { if(!window.confirm("Löschen?")) return; const emp = employees.find(e => e.id === empId); if(emp) saveEmployee({ ...emp, vacationBookings: emp.vacationBookings.filter(b => b.id !== bookingId) }); };

  const bookSick = (empId) => {
      if (!sickForm.start || !sickForm.end) return; if (sickForm.end < sickForm.start) { alert("Fehler im Datum"); return; }
      const days = countWeekdays(sickForm.start, sickForm.end);
      const emp = employees.find(e => e.id === empId); if (!emp) return;
      const newBooking = { id: generateId(), startDate: sickForm.start, endDate: sickForm.end, daysUsed: days };
      saveEmployee({ ...emp, sickBookings: [...(emp.sickBookings || []), newBooking].sort((a,b) => a.startDate.localeCompare(b.startDate)) });
      setSickForm({ start: '', end: '' });
  };

  const deleteSick = (empId, bookingId) => { if(!window.confirm("Löschen?")) return; const emp = employees.find(e => e.id === empId); if(emp) saveEmployee({ ...emp, sickBookings: (emp.sickBookings || []).filter(b => b.id !== bookingId) }); };

  // Überstunden-Konto: Summe (Ist - Soll) über alle Arbeitstage seit Beginn der Aufzeichnung.
  // Urlaub, Krankheit und Feiertage zählen als erfüllt (0). Der heutige Tag wird nur gewertet,
  // wenn bereits gestempelt wurde. Ein manueller Startwert (overtimeAdjust, in Stunden) wird addiert.
  const berechneUeberstunden = (emp) => {
      if (!emp) return 0;
      const startWert = parseFloat(emp.overtimeAdjust) || 0;
      const dailySoll = (emp.weeklyHours || 40) / 5;
      const daten = [];
      (emp.logs || []).forEach(l => { if (l.startTime) daten.push(l.startTime.slice(0, 10)); });
      (emp.vacationBookings || []).forEach(b => daten.push(b.startDate));
      (emp.sickBookings || []).forEach(b => daten.push(b.startDate));
      if (daten.length === 0) return startWert;
      const ersterTagD = new Date(daten.sort()[0] + 'T00:00:00');
      const heute = new Date(); heute.setHours(0, 0, 0, 0);
      const heuteIso = heute.getFullYear() + '-' + String(heute.getMonth() + 1).padStart(2, '0') + '-' + String(heute.getDate()).padStart(2, '0');
      // Abrechnungszeitraum: beginnt am eingestellten Tag (Standard 21., da Abrechnung am 20.)
      const startTag = parseInt(adminSettings.abrechnungStartTag, 10) || 21;
      const periodStart = (heute.getDate() >= startTag)
          ? new Date(heute.getFullYear(), heute.getMonth(), startTag)
          : new Date(heute.getFullYear(), heute.getMonth() - 1, startTag);
      periodStart.setHours(0, 0, 0, 0);
      let saldoMin = 0;
      const cur = new Date(periodStart > ersterTagD ? periodStart : ersterTagD);
      cur.setHours(0, 0, 0, 0);
      let guard = 0;
      while (cur <= heute && guard < 4000) {
          guard++;
          const iso = cur.getFullYear() + '-' + String(cur.getMonth() + 1).padStart(2, '0') + '-' + String(cur.getDate()).padStart(2, '0');
          const dow = cur.getDay();
          cur.setDate(cur.getDate() + 1);
          if (dow === 0 || dow === 6) continue;
          const dayLogs = (emp.logs || []).filter(l => l.startTime.startsWith(iso));
          const istFeiertag = !!feiertagName(iso, bundesland);
          const istUrlaub = (emp.vacationBookings || []).some(b => iso >= b.startDate && iso <= b.endDate);
          const istKrank = (emp.sickBookings || []).some(b => iso >= b.startDate && iso <= b.endDate);
          if (dayLogs.length > 0) {
              let ist = 0; dayLogs.forEach(l => { ist += (l.durationMinutes || 0); });
              saldoMin += ist - dailySoll * 60;
          } else if (istFeiertag || istUrlaub || istKrank) {
              // als erfüllt gewertet -> 0
          } else if (iso === heuteIso) {
              // heutiger Tag ohne Stempelung wird noch nicht als Minus gewertet
          } else {
              saldoMin += 0 - dailySoll * 60;
          }
      }
      return startWert + saldoMin / 60;
  };

  const aktuellerAbrechnungszeitraum = () => {
      const startTag = parseInt(adminSettings.abrechnungStartTag, 10) || 21;
      const heute = new Date();
      const start = (heute.getDate() >= startTag)
          ? new Date(heute.getFullYear(), heute.getMonth(), startTag)
          : new Date(heute.getFullYear(), heute.getMonth() - 1, startTag);
      const ende = new Date(start.getFullYear(), start.getMonth() + 1, startTag - 1);
      const f = dt => String(dt.getDate()).padStart(2, '0') + '.' + String(dt.getMonth() + 1).padStart(2, '0') + '.' + dt.getFullYear();
      return f(start) + ' – ' + f(ende);
  };

  const handleStatusChange = (empId, action) => {
    const emp = employees.find(e => e.id === empId); if (!emp) return;
    const now = new Date().toISOString();
    let updatedLogs = [...(emp.logs || [])]; 
    let newStatus = emp.status; let newCurrentEntryId = emp.currentEntryId;

    if (action === 'start') { 
        const newEntry = { id: generateId(), type: 'work', startTime: now, totalPauseMinutes: 0 };
        updatedLogs.unshift(newEntry); newStatus = 'working'; newCurrentEntryId = newEntry.id; 
        if (appMode === 'terminal') setTerminalMessage({ text: `Hallo ${emp.name}!`, type: 'success' }); 
    } 
    else if (action === 'pause_start') { 
        if(emp.currentEntryId) updatedLogs = updatedLogs.map(l => l.id === emp.currentEntryId ? { ...l, lastPauseStart: now } : l); 
        newStatus = 'break'; 
        if(appMode==='terminal') setTerminalMessage({text:'Pause gestartet', type:'success'}); 
    }
    else if (action === 'pause_end') { 
        if(emp.currentEntryId) updatedLogs = updatedLogs.map(l => l.id===emp.currentEntryId && l.lastPauseStart ? { ...l, totalPauseMinutes: l.totalPauseMinutes + calculateMinutesDiff(l.lastPauseStart, now), lastPauseStart: null } : l); 
        newStatus = 'working'; 
        if(appMode==='terminal') setTerminalMessage({text:'Willkommen zurück', type:'success'}); 
    }
    else if (action === 'end') { 
        if(emp.currentEntryId) updatedLogs = updatedLogs.map(l => { 
            if(l.id===emp.currentEntryId) { 
                const total = calculateMinutesDiff(l.startTime, now); 
                let pause = l.totalPauseMinutes; 
                if(l.lastPauseStart) pause += calculateMinutesDiff(l.lastPauseStart, now); 
                return { ...l, endTime: now, durationMinutes: Math.max(0, total-pause), totalPauseMinutes: pause, lastPauseStart: null }; 
            } 
            return l; 
        }); 
        newStatus = 'idle'; newCurrentEntryId = null; 
        if(appMode==='terminal') setTerminalMessage({text:`Tschüss ${emp.name}!`, type:'success'}); 
    }
    saveEmployee({ ...emp, status: newStatus, logs: updatedLogs, currentEntryId: newCurrentEntryId });
  };
  
  // ROBUSTER FIX: Das Bearbeiten der Zeiten wurde gegen fehlerhafte Formate abgesichert
  const saveEntry = () => { 
      const { empId, logId, mode, data } = editModal; 
      const emp = employees.find(e => e.id === empId); 
      if (!emp) return; 
      
      const startDateTime = new Date(`${data.date}T${data.startTime}`); 
      if (isNaN(startDateTime.getTime())) {
          alert("Fehler: Bitte überprüfe die Startzeit.");
          return;
      }

      let endDateTime = null;
      if (data.endTime) {
          endDateTime = new Date(`${data.date}T${data.endTime}`);
          if (isNaN(endDateTime.getTime())) {
              alert("Fehler: Bitte überprüfe die Endzeit.");
              return;
          }
      }

      let updatedLogs = [...(emp.logs || [])]; 
      let duration = 0; 
      if(endDateTime) {
          duration = Math.max(0, Math.floor((endDateTime.getTime()-startDateTime.getTime())/60000) - (data.pauseMinutes || 0));
      }
      
      if(mode==='add') {
          updatedLogs.push({ id: generateId(), type: 'work', startTime: startDateTime.toISOString(), endTime: endDateTime ? endDateTime.toISOString() : null, totalPauseMinutes: data.pauseMinutes || 0, durationMinutes: duration, lastPauseStart: null });
      } else if(mode==='edit') {
          updatedLogs = updatedLogs.map(l => l.id===logId ? { ...l, startTime: startDateTime.toISOString(), endTime: endDateTime ? endDateTime.toISOString() : null, totalPauseMinutes: data.pauseMinutes || 0, durationMinutes: duration, lastPauseStart: null } : l);
      }
      
      updatedLogs.sort((a,b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
      saveEmployee({ ...emp, logs: updatedLogs }); 
      setEditModal({...editModal, isOpen: false});
  };
  
  const deleteLogEntry = (empId, logId) => { 
      if(!window.confirm("Diesen Eintrag wirklich löschen?")) return;
      const emp = employees.find(e => e.id === empId);
      if (!emp) return;
      const newLogs = (emp.logs || []).filter(l => l.id !== logId);
      let newStatus = emp.status;
      let newCurrentEntryId = emp.currentEntryId;
      if (emp.currentEntryId === logId) { newStatus = 'idle'; newCurrentEntryId = null; }
      saveEmployee({ ...emp, logs: newLogs, status: newStatus, currentEntryId: newCurrentEntryId });
  };

  const toLocalISODate = (date) => {
      return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  };

  const getDateRange = (range, customRange) => {
      const now = new Date();
      let start = new Date(now); start.setHours(0,0,0,0);
      let end = new Date(now); end.setHours(23,59,59,999);
      
      if (range === 'today') {
      } else if (range === 'week') {
          const day = now.getDay();
          const diff = now.getDate() - day + (day === 0 ? -6 : 1);
          start.setDate(diff);
          end = new Date(start); end.setDate(start.getDate() + 4);
      } else if (range === 'month') {
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else if (range === 'year' || range === 'all') {
          start = new Date(now.getFullYear(), 0, 1);
          end = new Date(now.getFullYear(), 11, 31);
      } else if (range === 'custom') {
          start = new Date(customRange.start); start.setHours(0,0,0,0);
          end = new Date(customRange.end); end.setHours(23,59,59,999);
      }
      return { startStr: toLocalISODate(start), endStr: toLocalISODate(end) };
  };

  const exportExcel = async (empId = null) => {
      if (!window.XLSX) {
          await new Promise((resolve) => {
              const script = document.createElement('script');
              script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
              script.onload = resolve;
              document.head.appendChild(script);
          });
      }

      const { startStr, endStr } = getDateRange(timeRange, customDateRange);
      const start = new Date(startStr);
      const end = new Date(endStr);
      const dates = [];
      let curr = new Date(start);
      while (curr <= end) {
          dates.push(new Date(curr));
          curr.setDate(curr.getDate() + 1);
      }

      const wb = window.XLSX.utils.book_new();
      const empsToExport = empId ? [employees.find(e => e.id === empId)] : employees;

      empsToExport.forEach(emp => {
          if (!emp) return;
          const dailySoll = (emp.weeklyHours || 40) / 5;
          const sheetName = emp.name.replace(/[\\/?*[\]:]/g, '').substring(0, 31) || 'Tabelle1';
          
          const wsData = [
              ["Name des Mitarbeiters:", emp.name],
              [],
              ["Datum", "Start Uhrzeit", "Ende Uhrzeit", "Pause (Min)", "Gearbeitete Stunden", "Soll Arbeitszeit", "Ist Arbeitszeit", "Über-/Minusstunden", "Status"]
          ];

          let totalIst = 0;
          let totalSoll = 0;
          let totalDiff = 0;

          dates.forEach(d => {
              const dateIso = d.toISOString().split('T')[0];
              const dayOfWeek = d.getDay();
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              
              const dayLogs = (emp.logs || []).filter(l => l.startTime.startsWith(dateIso));
              let isVacation = (emp.vacationBookings || []).some(b => dateIso >= b.startDate && dateIso <= b.endDate);
              let isSick = (emp.sickBookings || []).some(b => dateIso >= b.startDate && dateIso <= b.endDate);
              const fName = isWeekend ? null : feiertagName(dateIso, bundesland);

              let tagesSoll = isWeekend ? 0 : dailySoll;
              let status = isWeekend ? "Wochenende" : "Abwesend";
              
              let startTimes = [];
              let endTimes = [];
              let pauseMins = 0;
              let istMins = 0;

              if (fName && dayLogs.length === 0) {
                  status = fName;
                  istMins = tagesSoll * 60;
              } else if (isVacation) {
                  status = "Urlaub";
                  istMins = tagesSoll * 60;
              } else if (isSick) {
                  status = "Krank";
                  istMins = tagesSoll * 60;
              } else if (dayLogs.length > 0) {
                  status = fName ? "Anwesend (Feiertag)" : "Anwesend";
                  dayLogs.forEach(l => {
                      startTimes.push(new Date(l.startTime).toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'}));
                      if (l.endTime) endTimes.push(new Date(l.endTime).toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'}));
                      pauseMins += (l.totalPauseMinutes || 0);
                      istMins += (l.durationMinutes || 0);
                  });
              }

              let tagesIst = istMins / 60;
              let differenz = tagesIst - tagesSoll;

              totalIst += tagesIst;
              totalSoll += tagesSoll;
              totalDiff += differenz;

              const startStrOutput = startTimes.length > 0 ? startTimes.join(', ') : "-";
              const endStrOutput = endTimes.length > 0 ? endTimes.join(', ') : "-";

              wsData.push([
                  formatDateGerman(dateIso),
                  startStrOutput,
                  endStrOutput,
                  pauseMins,
                  parseFloat(tagesIst.toFixed(2)),
                  parseFloat(tagesSoll.toFixed(2)),
                  parseFloat(tagesIst.toFixed(2)),
                  parseFloat(differenz.toFixed(2)),
                  status
              ]);
          });

          wsData.push([]);
          wsData.push([
              "GESAMT", "", "", "", 
              parseFloat(totalIst.toFixed(2)), 
              parseFloat(totalSoll.toFixed(2)), 
              parseFloat(totalIst.toFixed(2)), 
              parseFloat(totalDiff.toFixed(2)), 
              ""
          ]);

          const ws = window.XLSX.utils.aoa_to_sheet(wsData);
          const wscols = [{wch: 15},{wch: 15},{wch: 15},{wch: 15},{wch: 22},{wch: 20},{wch: 20},{wch: 25},{wch: 18}];
          ws['!cols'] = wscols;

          window.XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      const fileName = empId 
          ? `Zeiten_${empsToExport[0].name.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx` 
          : `Gesamtzeiten_${getRangeLabel().replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;

      window.XLSX.writeFile(wb, fileName);
  };
  
  const handleImportDataToDb = (event) => { 
      const file = event.target.files ? event.target.files[0] : null;
      if (!file) return; 
      const reader = new FileReader(); 
      reader.onload = async (e) => { 
          try { 
              const result = e.target ? e.target.result : null;
              if(typeof result === 'string') {
                  const importedData = JSON.parse(result); 
                  if (Array.isArray(importedData) && window.confirm(`Soll ${importedData.length} Mitarbeiter in die Cloud geladen werden?`)) { 
                      setLoading(true); 
                      for (const emp of importedData) { await saveEmployee(emp); } 
                      setShowSyncModal(false); setLoading(false); alert('Daten erfolgreich in die Cloud geladen!'); 
                  } 
              }
          } catch (error) { console.error(error); alert('Fehler beim Lesen der Datei.'); } 
      }; 
      reader.readAsText(file); 
      event.target.value = ''; 
  };

  const openAddEntryModal = (empId) => {
      const today = new Date().toISOString().split('T')[0];
      setEditModal({ isOpen: true, mode: 'add', empId: empId, data: { date: today, startTime: '08:00', endTime: '16:00', pauseMinutes: 30 } });
  };

  const openEditEntryModal = (empId, log) => {
      const date = getISODatePart(log.startTime);
      const startTime = getISOTimePart(log.startTime);
      const endTime = log.endTime ? getISOTimePart(log.endTime) : ''; 
      setEditModal({ isOpen: true, mode: 'edit', empId: empId, logId: log.id, data: { date, startTime, endTime, pauseMinutes: log.totalPauseMinutes } });
  };

  useEffect(() => {
    if (terminalMessage) {
      const timer = setTimeout(() => {
        setTerminalMessage(null);
        if (terminalMessage.type === 'success') setSelectedEmployeeId(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [terminalMessage]);

  // ROBUSTER FIX: Korrekturantrag absichern
  const handleCreateRequest = () => {
      const istPause = requestForm.type === 'break';
      if (!requestForm.date || !requestForm.reason || (istPause ? (requestForm.minutes === '' || requestForm.minutes === null || requestForm.minutes === undefined) : !requestForm.time)) { alert("Bitte alle Felder ausfüllen."); return; }
      const emp = employees.find(e => e.id === selectedEmployeeId); if (!emp) return;

      const newRequest = { id: generateId(), date: requestForm.date, time: istPause ? '' : requestForm.time, minutes: istPause ? (parseInt(requestForm.minutes, 10) || 0) : null, type: requestForm.type, reason: requestForm.reason, status: 'pending', createdAt: new Date().toISOString() };
      const updatedEmp = { ...emp, requests: [...(emp.requests || []), newRequest] };
      
      saveEmployee(updatedEmp); 
      setShowRequestModal(false); 
      setTerminalMessage({ text: "Antrag erfolgreich gesendet!", type: "success" });
  };

  // ROBUSTER FIX: Admin Bewilligung absichern
  const handleApproveRequest = (empId, request) => {
      const emp = employees.find(e => e.id === empId); if (!emp) return;
      let updatedLogs = [...(emp.logs || [])];
      const targetDateStr = request.date;
      const targetTimeStr = request.time;

      // Pausen-Korrektur: setzt die Pausenminuten des Tages und rechnet die Netto-Zeit neu
      if (request.type === 'break') {
          const idx = updatedLogs.findIndex(l => l.startTime.startsWith(targetDateStr));
          if (idx === -1) { alert("Für diesen Tag gibt es keinen Zeiteintrag, dem die Pause zugeordnet werden kann."); return; }
          const log = { ...updatedLogs[idx] };
          log.totalPauseMinutes = Math.max(0, parseInt(request.minutes, 10) || 0);
          if (log.startTime && log.endTime) {
              const diffMins = Math.floor((new Date(log.endTime).getTime() - new Date(log.startTime).getTime()) / 60000);
              log.durationMinutes = Math.max(0, diffMins - log.totalPauseMinutes);
          }
          updatedLogs[idx] = log;
          updatedLogs.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
          const reqs = (emp.requests || []).map(r => r.id === request.id ? { ...r, status: 'approved' } : r);
          saveEmployee({ ...emp, logs: updatedLogs, requests: reqs });
          return;
      }

      const fullDateTime = new Date(`${targetDateStr}T${targetTimeStr}`);
      if (isNaN(fullDateTime.getTime())) {
          alert("Fehler: Der Antrag enthält eine ungültige Uhrzeit.");
          return;
      }
      const fullDateTimeIso = fullDateTime.toISOString();

      let logIndex = updatedLogs.findIndex(l => l.startTime.startsWith(targetDateStr));
      
      if (logIndex === -1) {
          const newLog = { id: generateId(), type: 'work', startTime: request.type === 'start' ? fullDateTimeIso : new Date(`${targetDateStr}T08:00`).toISOString(), endTime: request.type === 'end' ? fullDateTimeIso : null, totalPauseMinutes: 0, durationMinutes: 0, lastPauseStart: null };
          updatedLogs.push(newLog); logIndex = updatedLogs.length - 1;
      } else {
          let log = { ...updatedLogs[logIndex] };
          if (request.type === 'start') log.startTime = fullDateTimeIso; else if (request.type === 'end') log.endTime = fullDateTimeIso;
          updatedLogs[logIndex] = log;
      }
      const log = updatedLogs[logIndex];
      if (log.startTime && log.endTime) {
          const diffMins = Math.floor((new Date(log.endTime).getTime() - new Date(log.startTime).getTime()) / 1000 / 60);
          log.durationMinutes = Math.max(0, diffMins - (log.totalPauseMinutes || 0));
      }
      updatedLogs.sort((a,b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
      const updatedRequests = (emp.requests || []).map(r => r.id === request.id ? { ...r, status: 'approved' } : r);
      saveEmployee({ ...emp, logs: updatedLogs, requests: updatedRequests });
  };

  const handleRejectRequest = (empId, requestId) => {
      const emp = employees.find(e => e.id === empId); if (!emp) return;
      const updatedRequests = (emp.requests || []).map(r => r.id === requestId ? { ...r, status: 'rejected' } : r);
      saveEmployee({ ...emp, requests: updatedRequests });
  };

  const renderDashboard = () => (
      <div className="space-y-6 animate-in fade-in pb-20">
          <div className="flex justify-between p-4 bg-white rounded-xl border shadow-sm"><div><h3 className="font-bold text-lg">Übersicht ({getRangeLabel()})</h3><p className="text-sm text-slate-500">Filter: {timeRange}</p></div><button onClick={() => exportExcel()} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"><Download className="w-4 h-4" /> Excel Export</button></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
             <div className="bg-white p-6 rounded-xl shadow-sm border"><div className="text-3xl font-bold">{dashboardStats.totalHours.toFixed(1)} <span className="text-sm font-normal">Std</span></div><p className="text-xs text-slate-400">Gesamt</p></div>
             <div className="bg-white p-6 rounded-xl shadow-sm border"><div className="text-3xl font-bold">{dashboardStats.avgHoursPerDay.toFixed(1)} <span className="text-sm font-normal">Std</span></div><p className="text-xs text-slate-400">Ø pro Tag / MA</p></div>
             <div className="bg-white p-6 rounded-xl shadow-sm border"><div className="text-3xl font-bold">{dashboardStats.activeEmployeeCount}</div><p className="text-xs text-slate-400">Aktiv</p></div>
             <div className="bg-white p-6 rounded-xl shadow-sm border"><div className="text-3xl font-bold">{dashboardStats.vacationCount}</div><p className="text-xs text-slate-400">Urlaub</p></div>
          </div>

          {dashboardStats.pendingRequests > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-orange-800 flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5"/> Offene Korrekturanträge ({dashboardStats.pendingRequests})</h3>
                  <div className="space-y-3">
                      {employees.map(emp => (
                          (emp.requests || []).filter(r => r.status === 'pending').map(req => (
                              <div key={req.id} className="bg-white p-4 rounded-lg border border-orange-100 flex justify-between items-center shadow-sm">
                                  <div>
                                      <span className="font-bold text-slate-700">{emp.name}</span>
                                      <span className="text-sm text-slate-500 mx-2">|</span>
                                      <span className="text-sm text-slate-600">{formatDateGerman(req.date)}: 
                                          {req.type === 'start' ? ' Startzeit ändern' : req.type === 'end' ? ' Endzeit ändern' : ' Pause korrigieren'} auf
                                          <strong>{req.type === 'break' ? ` ${req.minutes} Min` : ` ${req.time} Uhr`}</strong>
                                      </span>
                                      <p className="text-xs text-slate-400 mt-1 italic">Grund: {req.reason}</p>
                                  </div>
                                  <div className="flex gap-2">
                                      <button onClick={() => handleApproveRequest(emp.id, req)} className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200" title="Genehmigen"><Check size={18}/></button>
                                      <button onClick={() => handleRejectRequest(emp.id, req.id)} className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200" title="Ablehnen"><X size={18}/></button>
                                  </div>
                              </div>
                          ))
                      ))}
                  </div>
              </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100"><h3 className="text-lg font-semibold text-slate-800">Mitarbeiter Übersicht</h3></div>
              <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 text-slate-500">
                          <tr><th className="px-6 py-3 font-medium">Name</th><th className="px-6 py-3 font-medium">Anstellung</th><th className="px-6 py-3 font-medium text-right">Stunden</th><th className="px-6 py-3 font-medium text-right">Ø Täglich</th><th className="px-6 py-3 font-medium text-right">Resturlaub</th><th className="px-6 py-3 font-medium text-right">Aktion</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {employees.map(emp => {
                            const empLogs = filterLogsByRange(emp.logs || [], timeRange);
                            const totalMins = empLogs.reduce((sum, log) => sum + (log.durationMinutes || 0), 0);
                            const uniqueDays = new Set(empLogs.map(l => l.startTime.split('T')[0])).size;
                            const avgMins = uniqueDays > 0 ? totalMins / uniqueDays : 0;
                            const usedVacation = (emp.vacationBookings || []).reduce((sum, b) => sum + b.daysUsed, 0);
                            const remainingVacation = emp.vacationEntitlement - usedVacation;
                            const onVacation = isTodayVacation(emp.vacationBookings);
                            const currentLog = (emp.logs || [])[0];
                            const isStale = emp.status !== 'idle' && currentLog && !isDateToday(currentLog.startTime);

                            return (
                                <tr key={emp.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedEmployeeId(emp.id)}>
                                    <td className="px-6 py-4 font-medium text-slate-800 flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${isStale ? 'bg-red-500' : onVacation ? 'bg-orange-400' : emp.status === 'working' ? 'bg-green-500' : emp.status === 'break' ? 'bg-yellow-500' : 'bg-slate-300'}`} />
                                        {emp.name}
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">{getEmploymentLabel(emp.employmentType)} <span className="text-slate-400 text-xs">({emp.weeklyHours}h)</span></td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-700 font-bold">{formatDecimalDuration(totalMins)}</td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-500">{formatDecimalDuration(avgMins)}</td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-500">{remainingVacation} Tage</td>
                                    <td className="px-6 py-4 text-right">
                                        {isStale ? <span className="text-red-600 font-bold text-xs bg-red-100 px-2 py-1 rounded">⚠ {formatDateGerman(currentLog?.startTime?.split('T')[0])}</span> : <span className="text-blue-600 font-medium text-xs bg-blue-50 px-2 py-1 rounded">Details</span>}
                                    </td>
                                </tr>
                            );
                        })}
                      </tbody>
                  </table>
              </div>
          </div>
      </div>
  );

  const renderAdminView = () => {
    const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
    const filteredLogs = selectedEmployee ? filterLogsByRange(selectedEmployee.logs || [], timeRange) : [];
    const totalMinutesInPeriod = filteredLogs.reduce((sum, log) => sum + (log.durationMinutes || 0), 0);
    const uniqueWorkDays = new Set(filteredLogs.map(log => log.startTime.split('T')[0])).size;
    const averageMinutesPerDay = uniqueWorkDays > 0 ? totalMinutesInPeriod / uniqueWorkDays : 0;
    const usedVacation = selectedEmployee ? (selectedEmployee.vacationBookings || []).reduce((sum, b) => sum + b.daysUsed, 0) : 0;
    const remainingVacation = selectedEmployee ? selectedEmployee.vacationEntitlement - usedVacation : 0;
    const isCurrentlyOnVacation = selectedEmployee ? isTodayVacation(selectedEmployee.vacationBookings) : false;

    return (
      <main className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3 space-y-6">
           <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col lg:sticky lg:top-20" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
             <button onClick={() => setSelectedEmployeeId(null)} className="w-full flex gap-2 p-3 bg-blue-50 text-blue-700 rounded-lg mb-4 font-bold"><BarChart3 size={20}/> Dashboard</button>
             <h2 className="text-xs font-bold text-slate-400 uppercase mb-2">Mitarbeiter</h2>
             <form onSubmit={addEmployee} className="flex gap-2 mb-4"><input type="text" placeholder="Neu..." className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" value={newEmployeeName} onChange={(e) => setNewEmployeeName(e.target.value)} /><button type="submit" className="bg-blue-600 text-white p-1.5 rounded-lg hover:bg-blue-700 transition-colors" disabled={!newEmployeeName.trim()}><Plus className="w-4 h-4" /></button></form>
             <div className="space-y-1 flex-1 overflow-y-auto allow-scroll">
               {employees.map(emp => (
                 <div key={emp.id} onClick={() => setSelectedEmployeeId(emp.id)} className={`p-2.5 rounded-lg cursor-pointer flex items-center justify-between border ${selectedEmployeeId===emp.id ? 'bg-blue-50 border-blue-500' : 'bg-white border-transparent'}`}>
                    <span className="truncate font-medium text-sm flex items-center gap-2">
                        {emp.name} 
                        {emp.userPin && <Lock className="w-3 h-3 text-blue-500" />}
                    </span>
                    {selectedEmployeeId === emp.id && (<button onClick={(e) => { e.stopPropagation(); requestDeleteEmployee(emp.id); }} className="text-slate-400 hover:text-red-500 p-1 ml-1" title="Löschen"><Trash2 className="w-3 h-3" /></button>)}
                 </div>
               ))}
             </div>
             <div className="pt-4 border-t border-slate-100 mt-4 space-y-2">
                 <button onClick={() => setShowSyncModal(true)} className="flex items-center gap-2 text-slate-500 hover:text-blue-600 text-sm px-2 py-2 w-full rounded-lg hover:bg-blue-50 transition-colors"><Database className="w-4 h-4" /> Datenabgleich</button>
                 <button onClick={() => {setNewPinInput(adminSettings.adminPin); setShowSettingsModal(true)}} className="flex items-center gap-2 text-sm text-slate-500 p-2 hover:bg-slate-50 rounded w-full"><IconSettings size={16}/> Einstellungen</button>
             </div>
           </div>
        </div>
        
        <div className="lg:col-span-9 pb-20">
            <div className="flex flex-col md:flex-row md:items-center gap-4 bg-white p-3 rounded-xl border border-slate-200 mb-6 shadow-sm">
                <div className="flex gap-1 overflow-x-auto pb-2 md:pb-0">{['today', 'week', 'month', 'year', 'all', 'custom'].map((range) => (<button key={range} onClick={() => setTimeRange(range)} className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors whitespace-nowrap ${timeRange === range ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{range === 'today' && 'Heute'}{range === 'week' && 'Woche'}{range === 'month' && 'Monat'}{range === 'year' && 'Jahr'}{range === 'all' && 'Gesamt'}{range === 'custom' && 'Benutzerdefiniert'}</button>))}</div>
                {timeRange === 'custom' && (<div className="flex items-center gap-2 text-sm border-l pl-4 border-slate-200"><input type="date" className="border border-slate-300 rounded px-2 py-1 text-slate-700" value={customDateRange.start} onChange={(e) => setCustomDateRange({...customDateRange, start: e.target.value})} /><span className="text-slate-400">-</span><input type="date" className="border border-slate-300 rounded px-2 py-1 text-slate-700" value={customDateRange.end} onChange={(e) => setCustomDateRange({...customDateRange, end: e.target.value})} /></div>)}
            </div>

            {selectedEmployee ? (
                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border p-6 flex justify-between">
                        <div>
                            <h2 className="text-2xl font-bold flex items-center gap-2">
                                {selectedEmployee.name}
                                <button onClick={() => openEmployeeSettings(selectedEmployee)} className="p-1.5 bg-slate-100 rounded hover:bg-slate-200 text-slate-600"><UserCog size={18}/></button>
                            </h2>
                            <div className="flex gap-2 mt-2 text-sm text-slate-500">
                                <span className="px-2 py-0.5 bg-slate-100 rounded">{getEmploymentLabel(selectedEmployee.employmentType)}</span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                             <button onClick={handleForceReset} className="text-sm bg-red-50 text-red-700 px-3 py-2 rounded font-medium flex gap-1 items-center hover:bg-red-100 transition-colors"><RotateCcw size={16}/> Status Reset</button>
                             <button onClick={() => openAddEntryModal(selectedEmployee.id)} className="text-sm bg-slate-100 px-3 py-2 rounded font-medium flex gap-1 items-center"><Plus size={16}/> Manuell</button>
                             <button onClick={() => exportExcel(selectedEmployee.id)} className="text-sm bg-blue-50 text-blue-700 px-3 py-2 rounded font-medium flex gap-1 items-center"><Download size={16}/> Excel</button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl"><div className="flex items-center gap-2 text-blue-600 mb-1"><Clock className="w-4 h-4" /><div className="text-xs font-bold uppercase tracking-wider">Gesamtzeit</div></div><div className="text-2xl font-bold text-slate-800">{formatDecimalDuration(totalMinutesInPeriod)}</div><div className="text-xs text-blue-400">im gewählten Zeitraum</div></div>
                        <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl"><div className="flex items-center gap-2 text-indigo-600 mb-1"><TrendingUp className="w-4 h-4" /><div className="text-xs font-bold uppercase tracking-wider">Ø Täglich</div></div><div className="text-2xl font-bold text-slate-800">{formatDecimalDuration(averageMinutesPerDay)}</div><div className="text-xs text-indigo-400">Durchschnitt pro Arbeitstag</div></div>
                        <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl"><div className="flex items-center gap-2 text-slate-500 mb-1"><CalendarDays className="w-4 h-4" /><div className="text-xs font-bold uppercase tracking-wider">Arbeitstage</div></div><div className="text-2xl font-bold text-slate-800">{uniqueWorkDays}</div><div className="text-xs text-slate-400">Tage mit Zeiteinträgen</div></div>
                    </div>
                    
                    {(() => { const konto = berechneUeberstunden(selectedEmployee); const pos = konto >= 0; return (
                    <div className={`rounded-lg p-4 mb-6 border ${pos ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2"><Wallet className={`w-5 h-5 ${pos ? 'text-emerald-600' : 'text-rose-600'}`} /><div><h4 className="text-sm font-bold text-slate-800">Überstunden-Konto</h4><p className="text-xs text-slate-500">Abrechnungszeitraum {aktuellerAbrechnungszeitraum()}</p></div></div>
                            <div className={`text-2xl font-bold ${pos ? 'text-emerald-600' : 'text-rose-600'}`}>{pos ? '+' : ''}{konto.toFixed(1)} Std</div>
                        </div>
                    </div>
                    ); })()}

                    <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-6">
                        <div className="flex items-center justify-between">
                             <div><h4 className="text-sm font-bold text-orange-800 flex items-center gap-2"><Palmtree className="w-4 h-4" /> Urlaubsverwaltung</h4><p className="text-xs text-orange-700 mt-1">Anspruch: <strong>{selectedEmployee.vacationEntitlement}</strong> Tage | Verbraucht: <strong>{usedVacation}</strong> | Rest: <strong>{remainingVacation}</strong></p></div>
                             <div className="flex gap-2 items-end"><div><label className="block text-xs text-orange-600 mb-1">Von</label><input type="date" value={vacationForm.start} onChange={e => setVacationForm({...vacationForm, start: e.target.value})} className="px-2 py-1 text-sm border rounded" /></div><div><label className="block text-xs text-orange-600 mb-1">Bis</label><input type="date" value={vacationForm.end} onChange={e => setVacationForm({...vacationForm, end: e.target.value})} className="px-2 py-1 text-sm border rounded" /></div><button onClick={() => bookVacation(selectedEmployee.id)} className="bg-orange-500 text-white px-3 py-1 rounded text-sm hover:bg-orange-600 h-[30px]">Buchen</button></div>
                        </div>
                        {(selectedEmployee.vacationBookings || []).length > 0 && (<div className="mt-3 pt-3 border-t border-orange-200 flex flex-wrap gap-2">{(selectedEmployee.vacationBookings || []).map(b => (<span key={b.id} className="inline-flex items-center gap-2 bg-white border border-orange-200 px-2 py-1 rounded text-xs text-orange-800 shadow-sm">{formatDateGerman(b.startDate)} - {formatDateGerman(b.endDate)} ({b.daysUsed} Tage)<button onClick={() => deleteVacation(selectedEmployee.id, b.id)} className="text-red-400 hover:text-red-600"><XCircle className="w-3 h-3" /></button></span>))}</div>)}
                    </div>

                    <div className="bg-rose-50 border border-rose-100 rounded-lg p-4 mb-6">
                        <div className="flex items-center justify-between">
                             <div><h4 className="text-sm font-bold text-rose-800 flex items-center gap-2"><Thermometer className="w-4 h-4" /> Krankheit</h4><p className="text-xs text-rose-700 mt-1">Krank gemeldet: <strong>{(selectedEmployee.sickBookings || []).reduce((s,b)=>s+b.daysUsed,0)}</strong> Tage (zählt als erfüllt – keine Minusstunden)</p></div>
                             <div className="flex gap-2 items-end"><div><label className="block text-xs text-rose-600 mb-1">Von</label><input type="date" value={sickForm.start} onChange={e => setSickForm({...sickForm, start: e.target.value})} className="px-2 py-1 text-sm border rounded" /></div><div><label className="block text-xs text-rose-600 mb-1">Bis</label><input type="date" value={sickForm.end} onChange={e => setSickForm({...sickForm, end: e.target.value})} className="px-2 py-1 text-sm border rounded" /></div><button onClick={() => bookSick(selectedEmployee.id)} className="bg-rose-500 text-white px-3 py-1 rounded text-sm hover:bg-rose-600 h-[30px]">Eintragen</button></div>
                        </div>
                        {(selectedEmployee.sickBookings || []).length > 0 && (<div className="mt-3 pt-3 border-t border-rose-200 flex flex-wrap gap-2">{(selectedEmployee.sickBookings || []).map(b => (<span key={b.id} className="inline-flex items-center gap-2 bg-white border border-rose-200 px-2 py-1 rounded text-xs text-rose-800 shadow-sm">{formatDateGerman(b.startDate)} - {formatDateGerman(b.endDate)} ({b.daysUsed} Tage)<button onClick={() => deleteSick(selectedEmployee.id, b.id)} className="text-red-400 hover:text-red-600"><XCircle className="w-3 h-3" /></button></span>))}</div>)}
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                       <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-4"><FileText className="w-4 h-4" /> Lohnabrechnungen (Dokumente)</h4>
                       <div className="flex gap-2 items-end mb-4">
                           <div>
                               <label className="block text-xs text-slate-600 mb-1">Monat (z.B. 10/2023)</label>
                               <input type="month" value={payslipMonth} onChange={e => setPayslipMonth(e.target.value)} className="px-2 py-1 text-sm border rounded w-36" />
                           </div>
                           <div className="flex-1">
                               <label className="block text-xs text-slate-600 mb-1">PDF Datei (Max. 800 KB)</label>
                               <input type="file" accept=".pdf" ref={fileInputRefDocs} onChange={handlePayslipFileChange} className="w-full text-sm border rounded p-0.5 bg-white" />
                           </div>
                           <button onClick={uploadPayslip} disabled={!payslipFile || !payslipMonth} className="bg-blue-600 disabled:bg-slate-300 text-white px-4 py-1.5 rounded text-sm font-medium hover:bg-blue-700 transition-colors h-[34px] flex items-center gap-2"><UploadCloud className="w-4 h-4"/> Hochladen</button>
                       </div>
                
                       {activePayslips.length > 0 && (
                           <div className="space-y-2 mt-4 pt-4 border-t border-slate-200">
                               {activePayslips.map(p => (
                                   <div key={p.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                                       <div className="flex items-center gap-3">
                                           <FileText className="w-6 h-6 text-red-500" />
                                           <div>
                                               <p className="text-sm font-bold text-slate-700">{formatMonthName(p.month)}</p>
                                               <p className="text-xs text-slate-500 truncate w-32">{p.fileName}</p>
                                           </div>
                                       </div>
                                       <div className="flex gap-2">
                                           <button onClick={() => sendPayslipMail(p)} className="p-2 text-emerald-600 bg-emerald-50 rounded hover:bg-emerald-100 transition-colors" title="Per E-Mail an Mitarbeiter senden"><Mail className="w-4 h-4" /></button>
                                           <button onClick={() => downloadBase64PDF(p.fileData, p.fileName)} className="p-2 text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors" title="Herunterladen"><Download className="w-4 h-4" /></button>
                                           <button onClick={() => deletePayslip(p.id)} className="p-2 text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors" title="Löschen"><Trash2 className="w-4 h-4" /></button>
                                       </div>
                                   </div>
                               ))}
                           </div>
                       )}
                    </div>
                    
                    {!isCurrentlyOnVacation && (<div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-6">{selectedEmployee.status === 'idle' && (<button onClick={() => handleStatusChange(selectedEmployee.id, 'start')} className="col-span-2 flex items-center justify-center p-4 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm transition-all"><Play className="w-5 h-5 mr-2" /> <span className="font-bold">Kommen</span></button>)}{selectedEmployee.status === 'working' && (<><button onClick={() => handleStatusChange(selectedEmployee.id, 'pause_start')} className="flex items-center justify-center p-4 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg shadow-sm"><Coffee className="w-5 h-5 mr-2" /> <span className="font-bold">Pause</span></button><button onClick={() => handleStatusChange(selectedEmployee.id, 'end')} className="flex items-center justify-center p-4 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-sm"><Square className="w-5 h-5 mr-2" /> <span className="font-bold">Gehen</span></button></>)}{selectedEmployee.status === 'break' && (<><button onClick={() => handleStatusChange(selectedEmployee.id, 'pause_end')} className="flex items-center justify-center p-4 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm"><PlayCircle className="w-5 h-5 mr-2" /> <span className="font-bold">Weiter</span></button><button onClick={() => handleStatusChange(selectedEmployee.id, 'end')} className="flex items-center justify-center p-4 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-sm"><Square className="w-5 h-5 mr-2" /> <span className="font-bold">Gehen</span></button></>)}</div>)}

                     <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-6 border-b border-slate-100 flex items-center gap-2"><IconHistory className="w-5 h-5 text-slate-500" /><h3 className="text-lg font-semibold text-slate-800">Zeitprotokoll</h3></div>
                        <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 text-slate-500"><tr><th className="px-6 py-3 font-medium">Datum</th><th className="px-6 py-3 font-medium">Zeit</th><th className="px-6 py-3 font-medium">Netto</th><th className="px-6 py-3 text-right">Aktion</th></tr></thead><tbody>{filteredLogs.map(log => (<tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50"><td className="px-6 py-4">{formatDate(log.startTime)}</td><td className="px-6 py-4">{formatTime(log.startTime)} - {log.endTime ? formatTime(log.endTime) : '...'}</td><td className="px-6 py-4 font-mono font-bold">{log.durationMinutes ? formatDuration(log.durationMinutes) : '-'}</td><td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                            <button onClick={() => openEditEntryModal(selectedEmployee.id, log)} className="p-2 text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors" title="Bearbeiten"><Edit className="w-4 h-4" /></button>
                            <button onClick={(e) => { e.stopPropagation(); deleteLogEntry(selectedEmployee.id, log.id); }} className="p-2 text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors" title="Löschen"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        </td></tr>))}</tbody></table></div>
                     </div>
                </div>
            ) : renderDashboard()}
        </div>
      </main>
    );
  };

  const renderTerminalView = () => {
      const activeEmployee = employees.find(e => e.id === selectedEmployeeId);

      if (terminalMessage) {
        return (<div className="max-w-xl mx-auto mt-20 p-8 text-center"><div className={`rounded-2xl p-8 text-center shadow-lg border-2 animate-in fade-in zoom-in duration-300 ${terminalMessage.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}><CheckCircle2 className={`w-20 h-20 mx-auto mb-4 ${terminalMessage.type === 'success' ? 'text-green-500' : 'text-blue-500'}`} /><h2 className="text-3xl font-bold text-slate-800 mb-2">{terminalMessage.text}</h2><p className="text-slate-500">Das Terminal schließt sich gleich automatisch...</p></div></div>);
      }
      
      if (activeEmployee) {
          const isVacationToday = isTodayVacation(activeEmployee.vacationBookings);
          
          if (isVacationToday) {
              return (<div className="max-w-xl mx-auto mt-20 p-8"><div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-10 text-center shadow-lg"><Palmtree className="w-24 h-24 mx-auto mb-6 text-orange-500" /><h2 className="text-3xl font-bold text-orange-800 mb-4">Schönen Urlaub, {activeEmployee.name}!</h2><p className="text-orange-700 text-lg mb-8">Du bist heute als "im Urlaub" eingetragen. Genieße die freie Zeit!</p><button onClick={() => setSelectedEmployeeId(null)} className="bg-white border border-orange-200 text-orange-600 px-6 py-3 rounded-xl font-bold hover:bg-orange-100 transition-colors">Zurück</button></div></div>);
          }

          if (showUserTimesheet) {
              const filteredLogs = filterLogsByRange(activeEmployee.logs || [], timeRange);
              const totalMinutesInPeriod = filteredLogs.reduce((sum, log) => sum + (log.durationMinutes || 0), 0);
              const uniqueWorkDays = new Set(filteredLogs.map(log => log.startTime.split('T')[0])).size;
              const averageMinutesPerDay = uniqueWorkDays > 0 ? totalMinutesInPeriod / uniqueWorkDays : 0;
  
              return (
                <div className="max-w-4xl mx-auto mt-6 p-4 h-[calc(100vh-6rem)] flex flex-col">
                  <div className="flex justify-between items-center mb-6">
                     <button onClick={() => setShowUserTimesheet(false)} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors"><ArrowLeft className="w-5 h-5" /> Zurück zur Stempeluhr</button>
                     <h2 className="text-xl font-bold text-slate-800">Meine Zeiten</h2>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto allow-scroll pr-2 pb-20 space-y-6">
                      <div className="flex flex-col md:flex-row md:items-center gap-4 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                          <div className="flex gap-1 overflow-x-auto pb-2 md:pb-0">{['today', 'week', 'month', 'year', 'all', 'custom'].map((range) => (<button key={range} onClick={() => setTimeRange(range)} className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors whitespace-nowrap ${timeRange === range ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{range === 'today' && 'Heute'}{range === 'week' && 'Woche'}{range === 'month' && 'Monat'}{range === 'year' && 'Jahr'}{range === 'all' && 'Gesamt'}{range === 'custom' && 'Benutzerdefiniert'}</button>))}</div>
                          {timeRange === 'custom' && (<div className="flex items-center gap-2 text-sm border-l pl-4 border-slate-200"><input type="date" className="border border-slate-300 rounded px-2 py-1 text-slate-700" value={customDateRange.start} onChange={(e) => setCustomDateRange({...customDateRange, start: e.target.value})} /><span className="text-slate-400">-</span><input type="date" className="border border-slate-300 rounded px-2 py-1 text-slate-700" value={customDateRange.end} onChange={(e) => setCustomDateRange({...customDateRange, end: e.target.value})} /></div>)}
                      </div>
  
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl"><div className="flex items-center gap-2 text-blue-600 mb-1"><Clock className="w-4 h-4" /><div className="text-xs font-bold uppercase tracking-wider">Gesamtzeit</div></div><div className="text-2xl font-bold text-slate-800">{formatDecimalDuration(totalMinutesInPeriod)}</div><div className="text-xs text-blue-400">im gewählten Zeitraum</div></div>
                          <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl"><div className="flex items-center gap-2 text-indigo-600 mb-1"><TrendingUp className="w-4 h-4" /><div className="text-xs font-bold uppercase tracking-wider">Ø Täglich</div></div><div className="text-2xl font-bold text-slate-800">{formatDecimalDuration(averageMinutesPerDay)}</div><div className="text-xs text-indigo-400">Durchschnitt pro Arbeitstag</div></div>
                          <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl"><div className="flex items-center gap-2 text-slate-500 mb-1"><CalendarDays className="w-4 h-4" /><div className="text-xs font-bold uppercase tracking-wider">Arbeitstage</div></div><div className="text-2xl font-bold text-slate-800">{uniqueWorkDays}</div><div className="text-xs text-slate-400">Tage mit Zeiteinträgen</div></div>
                      </div>

                      <button onClick={() => setShowPayslipsUserModal(true)} className="w-full flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-blue-50 transition-colors mt-4">
                          <div className="flex items-center gap-4">
                              <div className="p-3 bg-blue-100 rounded-xl">
                                  <Folder className="w-6 h-6 text-blue-600" />
                              </div>
                              <div className="text-left">
                                  <h4 className="text-base font-bold text-slate-800">Meine Lohnabrechnungen</h4>
                                  <p className="text-xs text-slate-500">{activePayslips.length} Dokument(e) verfügbar</p>
                              </div>
                          </div>
                          <ArrowLeft className="w-5 h-5 text-slate-400 rotate-180" />
                      </button>
  
                      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                          <div className="p-4 border-b border-slate-100 flex items-center gap-2"><IconHistory className="w-5 h-5 text-slate-500" /><h3 className="text-lg font-semibold text-slate-800">Mein Zeitprotokoll</h3></div>
                          <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 text-slate-500"><tr><th className="px-6 py-3 font-medium">Datum</th><th className="px-6 py-3 font-medium">Zeit</th><th className="px-6 py-3 font-medium">Netto</th><th className="px-6 py-3 text-right">Pause</th></tr></thead><tbody>
                              {filteredLogs.length > 0 ? filteredLogs.map(log => (<tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50"><td className="px-6 py-4">{formatDate(log.startTime)}</td><td className="px-6 py-4">{formatTime(log.startTime)} - {log.endTime ? formatTime(log.endTime) : '...'}</td><td className="px-6 py-4 font-mono font-bold">{log.durationMinutes ? formatDuration(log.durationMinutes) : '-'}</td><td className="px-6 py-4 text-right text-slate-500">{log.totalPauseMinutes} Min</td></tr>)) : (<tr><td colSpan="4" className="px-6 py-8 text-center text-slate-400">Keine Einträge im gewählten Zeitraum.</td></tr>)}
                          </tbody></table></div>
                      </div>
                  </div>
                </div>
              );
          }

          let currentPauseInfo = "";
          if (activeEmployee.status === 'break') { 
              const currentLog = (activeEmployee.logs || [])[0]; 
              if (currentLog && currentLog.lastPauseStart) { 
                  const start = new Date(currentLog.lastPauseStart); 
                  currentPauseInfo = `Seit ${start.toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'})} Uhr`; 
              } 
          }

          return (
            <div className="max-w-2xl mx-auto mt-10 p-4">
              <button onClick={() => setSelectedEmployeeId(null)} className="mb-8 flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors"><ArrowLeft className="w-5 h-5" /> Zurück</button>
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
                <div className="bg-slate-50 p-8 border-b border-slate-100 text-center">
                    <h2 className="text-2xl font-bold text-slate-800">Hallo, {activeEmployee.name}</h2>
                    <p className="text-slate-500 mt-2 font-medium">
                        {activeEmployee.status === 'working' ? '✅ Du bist eingestempelt.' : 
                         activeEmployee.status === 'break' ? '☕ Du bist in der Pause.' : 
                         'Bitte wähle eine Aktion.'}
                    </p>
                </div>
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {activeEmployee.status === 'idle' && (<button onClick={() => handleStatusChange(activeEmployee.id, 'start')} className="col-span-2 p-8 rounded-xl border-2 flex flex-col items-center justify-center transition-all border-green-100 bg-green-50 hover:bg-green-100 hover:border-green-300 hover:shadow-md cursor-pointer"><Play className="w-12 h-12 mb-4 text-green-600" /><span className="text-xl font-bold text-green-800">KOMMEN</span><span className="text-xs text-green-600 mt-2">Arbeit beginnen</span></button>)}
                  {activeEmployee.status === 'working' && (<><button onClick={() => handleStatusChange(activeEmployee.id, 'pause_start')} className="p-8 rounded-xl border-2 flex flex-col items-center justify-center transition-all border-yellow-100 bg-yellow-50 hover:bg-yellow-100 hover:border-yellow-300 hover:shadow-md cursor-pointer"><Coffee className="w-12 h-12 mb-4 text-yellow-600" /><span className="text-xl font-bold text-yellow-800">PAUSE</span><span className="text-xs text-yellow-600 mt-2">Kurz ausruhen</span></button><button onClick={() => handleStatusChange(activeEmployee.id, 'end')} className="flex items-center justify-center p-4 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-sm"><Square className="w-12 h-12 mb-4 text-red-600" /><span className="text-xl font-bold text-red-800">GEHEN</span><span className="text-xs text-red-600 mt-2">Feierabend</span></button></>)}
                  {activeEmployee.status === 'break' && (<><button onClick={() => handleStatusChange(activeEmployee.id, 'pause_end')} className="p-8 rounded-xl border-2 flex flex-col items-center justify-center transition-all border-green-100 bg-green-50 hover:bg-green-100 hover:border-green-300 hover:shadow-md cursor-pointer"><PlayCircle className="w-12 h-12 mb-4 text-green-600" /><span className="text-xl font-bold text-green-800">PAUSE ENDE</span><span className="text-xs text-green-600 mt-2">Weiterarbeiten</span></button><button onClick={() => handleStatusChange(activeEmployee.id, 'end')} className="flex items-center justify-center p-4 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-sm"><Square className="w-12 h-12 mb-4 text-red-600" /><span className="text-xl font-bold text-red-800">GEHEN</span><span className="text-xs text-red-600 mt-2">Direkt heim</span></button></>)}
                </div>
                <div className="p-4 border-t border-slate-100 grid grid-cols-2 gap-4">
                    <button onClick={() => { setRequestForm({ date: new Date().toISOString().split('T')[0], time: '', type: 'start', reason: '' }); setShowRequestModal(true); }} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-50 text-slate-600 font-medium hover:bg-slate-100 transition-colors">
                        <FileText className="w-4 h-4" /> Korrekturantrag
                    </button>
                    <button onClick={() => setShowUserTimesheet(true)} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-blue-50 text-blue-600 font-medium hover:bg-blue-100 transition-colors">
                        <IconHistory className="w-4 h-4" /> Meine Zeiten
                    </button>
                </div>
                {activeEmployee.status === 'break' && (<div className="bg-yellow-50 px-8 py-4 text-center text-sm text-yellow-700 border-t border-yellow-100 animate-pulse font-medium"><Coffee className="w-4 h-4 inline mr-2" />Aktuell in Pause {currentPauseInfo ? `(${currentPauseInfo})` : ''}</div>)}
              </div>
            </div>
          );
      }

      return (
        <div className="max-w-md mx-auto px-4 py-8 flex flex-col items-center justify-center min-h-[80vh]">
            <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8 w-full">
                <div className="text-center mb-8">
                    <Clock className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                    <h2 className="text-2xl font-bold text-slate-800">ZeitManager</h2>
                    <p className="text-slate-400">
                        {employees.length === 0 ? "Warte auf Datenbank..." : "Bitte PIN eingeben"}
                    </p>
                </div>
                <div className="flex justify-center gap-4 mb-8">
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} className={`w-4 h-4 rounded-full border-2 ${terminalPinInput.length > i ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}></div>
                    ))}
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                        <button key={num} onClick={() => handleTerminalDigitClick(num.toString())} className="h-16 rounded-2xl bg-slate-50 hover:bg-slate-100 text-2xl font-bold text-slate-700 transition-colors shadow-sm border border-slate-100 active:scale-95">{num}</button>
                    ))}
                    <button onClick={() => setTerminalPinInput('')} className="h-16 rounded-2xl text-slate-400 hover:text-slate-600 font-medium">C</button>
                    <button onClick={() => handleTerminalDigitClick('0')} className="h-16 rounded-2xl bg-slate-50 hover:bg-slate-100 text-2xl font-bold text-slate-700 transition-colors shadow-sm border border-slate-100 active:scale-95">0</button>
                    <button onClick={handleTerminalBackspace} className="h-16 rounded-2xl text-slate-400 hover:text-slate-600 flex items-center justify-center"><Delete className="w-6 h-6"/></button>
                </div>
                <button onClick={handleTerminalLogin} disabled={terminalPinInput.length !== 4} className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${terminalPinInput.length === 4 ? 'bg-blue-600 text-white shadow-lg hover:bg-blue-700 active:scale-95' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
                   <CornerDownLeft className="w-5 h-5" /> Anmelden
                </button>
                {terminalError && <div className="mt-4 p-3 bg-red-50 text-red-600 text-center rounded-xl text-sm font-medium animate-in fade-in slide-in-from-top-2">{terminalError}</div>}
            </div>
        </div>
      );
  };

  const renderStaleFixModal = () => (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center border-l-4 border-red-500">
              <AlertOctagon className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-800 mb-2">Nicht abgemeldet!</h3>
              <p className="text-slate-600 mb-6">Du bist noch vom <strong>{staleEntry ? formatDateGerman(staleEntry.date) : ''}</strong> angemeldet. <br/>Bitte gib deine Endzeit (Feierabend) ein.</p>
              <input type="time" className="w-full text-center text-3xl font-bold p-4 border rounded-xl mb-6 bg-slate-50" value={staleEndTime} onChange={(e) => setStaleEndTime(e.target.value)} />
              <button onClick={handleFixStaleEntry} className="w-full py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors shadow-lg">Nachtragen & Anmelden</button>
          </div>
      </div>
  );

  const renderRequestModal = () => (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
              <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold text-slate-800">Korrekturantrag</h3>
                  <button onClick={() => setShowRequestModal(false)}><X className="text-slate-400" /></button>
              </div>
              <div className="space-y-4">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Datum</label><input type="date" className="w-full border rounded-lg p-2" value={requestForm.date} onChange={(e) => setRequestForm({...requestForm, date: e.target.value})} /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Was fehlt / ist falsch?</label><select className="w-full border rounded-lg p-2" value={requestForm.type} onChange={(e) => setRequestForm({...requestForm, type: e.target.value})}><option value="start">Startzeit (Kommen)</option><option value="end">Endzeit (Gehen)</option><option value="break">Pause</option></select></div>
                  {requestForm.type === 'break' ? (
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Pause (Minuten)</label><input type="number" min="0" step="5" placeholder="z.B. 30" className="w-full border rounded-lg p-2" value={requestForm.minutes} onChange={(e) => setRequestForm({...requestForm, minutes: e.target.value})} /></div>
                  ) : (
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Korrekte Uhrzeit</label><input type="time" className="w-full border rounded-lg p-2" value={requestForm.time} onChange={(e) => setRequestForm({...requestForm, time: e.target.value})} /></div>
                  )}
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Grund</label><textarea className="w-full border rounded-lg p-2 h-20" placeholder="z.B. Vergessen zu stempeln" value={requestForm.reason} onChange={(e) => setRequestForm({...requestForm, reason: e.target.value})}></textarea></div>
              </div>
              <div className="mt-6 flex justify-end gap-2"><button onClick={() => setShowRequestModal(false)} className="px-4 py-2 bg-slate-100 rounded text-slate-600">Abbrechen</button><button onClick={handleCreateRequest} className="px-4 py-2 bg-blue-600 text-white rounded shadow-sm">Beantragen</button></div>
          </div>
      </div>
  );

  const renderEmployeeSettingsModal = () => (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
              <div className="flex justify-between items-center mb-6"><h3 className="text-lg font-bold text-slate-800">Stammdaten</h3><button onClick={()=>setShowEmployeeSettingsModal(false)}><X className="text-slate-400"/></button></div>
              <div className="space-y-4">
                  <div><label className="text-sm font-bold text-slate-700">Name</label><input className="w-full border p-2 rounded" value={employeeEditForm.name} onChange={e=>setEmployeeEditForm({...employeeEditForm, name:e.target.value})}/></div>
                  <div><label className="text-sm font-bold text-slate-700">Vertrag</label><select className="w-full border p-2 rounded" value={employeeEditForm.type} onChange={e=>setEmployeeEditForm({...employeeEditForm, type:e.target.value})}><option value="fulltime">Vollzeit</option><option value="parttime">Teilzeit</option><option value="minijob">Aushilfe</option></select></div>
                  <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-sm font-bold text-slate-700">Std/Woche</label><input type="number" step="0.01" className="w-full border p-2 rounded" value={employeeEditForm.hours} onChange={e=>setEmployeeEditForm({...employeeEditForm, hours:e.target.value})}/></div>
                      <div><label className="text-sm font-bold text-slate-700">Urlaub</label><input type="number" step="0.5" className="w-full border p-2 rounded" value={employeeEditForm.vacation} onChange={e=>setEmployeeEditForm({...employeeEditForm, vacation:e.target.value})}/></div>
                  </div>
                  <div className="border-t pt-4">
                      <label className="text-sm font-bold text-slate-700 block mb-2">Login-PIN (4-stellig)</label>
                      <div className="flex items-center gap-2">
                          <input type="text" placeholder="0000" maxLength={4} className="w-full border p-2 rounded tracking-widest text-center font-mono text-lg" value={employeeEditForm.userPin || ''} onChange={e => setEmployeeEditForm({...employeeEditForm, userPin: e.target.value.replace(/[^0-9]/g, '')})} />
                          <Lock className="text-slate-400 w-5 h-5" />
                      </div>
                  </div>
                  <div className="border-t pt-4"><label className="text-sm font-bold text-slate-700 block mb-1">E-Mail (für Lohnabrechnung)</label><input type="email" placeholder="name@firma.de" className="w-full border p-2 rounded" value={employeeEditForm.email || ''} onChange={e => setEmployeeEditForm({...employeeEditForm, email: e.target.value})} /></div>
                  <div><label className="text-sm font-bold text-slate-700 block mb-1">Überstunden-Startwert (Std)</label><input type="number" step="0.1" className="w-full border p-2 rounded" value={employeeEditForm.overtimeAdjust} onChange={e => setEmployeeEditForm({...employeeEditForm, overtimeAdjust: e.target.value})} /><p className="text-xs text-slate-400 mt-1">Bestehendes Guthaben/Minus zu Beginn (optional).</p></div>
              </div>
              <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setShowEmployeeSettingsModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded">Abbrechen</button><button onClick={saveEmployeeSettings} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Speichern</button></div>
          </div>
      </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans select-none">
      <header className="bg-blue-900 text-white shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2"><Clock className="w-6 h-6 text-blue-300" /><h1 className="text-xl font-bold tracking-tight hidden sm:block">ZeitManager Pro</h1>{appMode === 'terminal' && <span className="bg-blue-800 text-xs px-2 py-1 rounded ml-2">Terminal Modus</span>}</div>
          <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${isCloudConfigured && db && user && !connectionError ? 'bg-green-400' : 'bg-red-400'}`}></div><span className="text-xs text-blue-200 hidden sm:inline">{isCloudConfigured && db && user && !connectionError ? 'Cloud verbunden' : 'Keine Verbindung'}</span></div>
              <button onClick={() => { if(appMode === 'terminal') { setShowPinModal(true); } else { setAppMode('terminal'); setSelectedEmployeeId(null); } }} className="flex items-center gap-2 text-sm bg-blue-800 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition-colors border border-blue-700">
                {appMode === 'admin' ? <Monitor className="w-4 h-4" /> : <LayoutDashboard className="w-4 h-4" />}{appMode === 'admin' ? 'Zum Terminal' : 'Zur Verwaltung'}
              </button>
          </div>
        </div>
      </header>

      {loading && (<div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div></div>)}
      {!loading && (appMode === 'admin' ? renderAdminView() : renderTerminalView())}

      {staleEntry && renderStaleFixModal()}
      {showRequestModal && renderRequestModal()}
      {showEmployeeSettingsModal && renderEmployeeSettingsModal()}

      {editModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
               <div className="flex justify-between items-center mb-6"><h3 className="text-lg font-bold text-slate-800">Zeit bearbeiten</h3><button onClick={()=>setEditModal({...editModal, isOpen:false})}><X/></button></div>
               <div className="space-y-4">
                   <input type="date" className="w-full border p-2 rounded select-text" value={editModal.data.date} onChange={e=>setEditModal({...editModal, data:{...editModal.data, date:e.target.value}})}/>
                   <div className="flex gap-2"><input type="time" className="w-full border p-2 rounded select-text" value={editModal.data.startTime} onChange={e=>setEditModal({...editModal, data:{...editModal.data, startTime:e.target.value}})}/><input type="time" className="w-full border p-2 rounded select-text" value={editModal.data.endTime} onChange={e=>setEditModal({...editModal, data:{...editModal.data, endTime:e.target.value}})}/></div>
                   <input type="number" className="w-full border p-2 rounded select-text" placeholder="Pause (Min)" value={editModal.data.pauseMinutes} onChange={e=>setEditModal({...editModal, data:{...editModal.data, pauseMinutes:parseInt(e.target.value)||0}})}/>
               </div>
               <div className="mt-6 flex justify-end gap-2"><button onClick={()=>setEditModal({...editModal, isOpen:false})} className="px-4 py-2 bg-slate-100 rounded">Abbrechen</button><button onClick={saveEntry} className="px-4 py-2 bg-blue-600 text-white rounded">Speichern</button></div>
           </div>
        </div>
      )}

      {showPinModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center">
             <Lock className="w-12 h-12 text-blue-600 mx-auto mb-4"/>
             <h3 className="text-xl font-bold mb-4">Admin Login</h3>
             <input type="password" placeholder="PIN" className="w-full text-center text-2xl p-3 border rounded-xl mb-4 select-text" value={pinInput} onChange={e=>setPinInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAdminAuth()} autoFocus/>
             <div className="flex justify-between"><button onClick={()=>setShowPinModal(false)} className="text-slate-500">Abbrechen</button><button onClick={handleAdminAuth} className="bg-blue-600 text-white px-6 py-2 rounded-lg">Login</button></div>
          </div>
        </div>
      )}

      {showUserPinModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center">
             <Lock className="w-12 h-12 text-green-600 mx-auto mb-4"/>
             <h3 className="text-xl font-bold mb-2">Anmeldung</h3>
             <p className="text-sm text-slate-500 mb-4">Bitte deine persönliche PIN eingeben.</p>
             <input type="password" placeholder="PIN" className="w-full text-center text-2xl p-3 border rounded-xl mb-4 select-text" value={pinInput} onChange={e=>setPinInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleUserPinSubmit()} autoFocus/>
             {pinError && <p className="text-red-500 text-sm mb-2">Falsche PIN</p>}
             <div className="flex justify-between"><button onClick={()=>{setShowUserPinModal(false); setPinInput('');}} className="text-slate-500">Abbrechen</button><button onClick={handleUserPinSubmit} className="bg-green-600 text-white px-6 py-2 rounded-lg">Bestätigen</button></div>
          </div>
        </div>
      )}

      {connectionError && (
          <div className="fixed bottom-4 right-4 bg-red-600 text-white p-4 rounded-xl shadow-lg flex items-center gap-3 animate-bounce">
              <AlertTriangle/>
              <div><p className="font-bold">Verbindung verloren</p><p className="text-xs">{connectionError}</p></div>
              <button onClick={forceReconnect}><RefreshCw/></button>
          </div>
      )}

      {showSyncModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
                  <div className="flex justify-between items-center mb-6"><div className="flex items-center gap-2"><Database className="w-6 h-6 text-blue-600" /><h3 className="text-lg font-bold text-slate-800">Datenabgleich</h3></div><button onClick={() => setShowSyncModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button></div>
                  <div className="space-y-4"><p className="text-sm text-slate-600">Lade hier deine JSON-Sicherung hoch, um die alten Daten in die neue Cloud-Datenbank zu übertragen.</p><div className="border-t border-slate-100 pt-4"><h4 className="text-sm font-semibold text-slate-800 mb-2">Sicherung einspielen</h4><button onClick={() => fileInputRef.current?.click()} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-50 text-green-700 hover:bg-green-100 rounded-xl transition-colors font-medium border border-green-200"><Cloud className="w-5 h-5" /> Sicherung hochladen</button><input type="file" ref={fileInputRef} onChange={handleImportDataToDb} accept=".json" className="hidden" /></div></div>
              </div>
          </div>
      )}

      {showSettingsModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
                  <div className="flex justify-between items-center mb-6"><h3 className="text-lg font-bold text-slate-800">Einstellungen</h3><button onClick={() => setShowSettingsModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button></div>
                  <div className="space-y-4"><div><label className="block text-sm font-medium text-slate-700 mb-1">Admin PIN ändern</label><div className="relative"><KeyRound className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" /><input type="text" className="w-full border rounded-lg p-2 pl-9 select-text" value={newPinInput} onChange={(e) => setNewPinInput(e.target.value)} placeholder="0000" /></div><p className="text-xs text-slate-500 mt-1">Diese PIN wird benötigt, um den Terminal-Modus zu verlassen.</p></div><div><label className="block text-sm font-medium text-slate-700 mb-1">Bundesland (für Feiertage)</label><select className="w-full border rounded-lg p-2" value={adminSettings.bundesland || 'HE'} onChange={(e) => updateSettings({ ...adminSettings, bundesland: e.target.value })}><option value="BW">Baden-Württemberg</option><option value="BY">Bayern</option><option value="BE">Berlin</option><option value="BB">Brandenburg</option><option value="HB">Bremen</option><option value="HH">Hamburg</option><option value="HE">Hessen</option><option value="MV">Mecklenburg-Vorpommern</option><option value="NI">Niedersachsen</option><option value="NW">Nordrhein-Westfalen</option><option value="RP">Rheinland-Pfalz</option><option value="SL">Saarland</option><option value="SN">Sachsen</option><option value="ST">Sachsen-Anhalt</option><option value="SH">Schleswig-Holstein</option><option value="TH">Thüringen</option></select><p className="text-xs text-slate-500 mt-1">Legt fest, welche gesetzlichen Feiertage berücksichtigt werden.</p></div><div><label className="block text-sm font-medium text-slate-700 mb-1">Abrechnungszeitraum beginnt am Tag</label><input type="number" min="1" max="28" className="w-full border rounded-lg p-2" value={adminSettings.abrechnungStartTag || 21} onChange={(e) => updateSettings({ ...adminSettings, abrechnungStartTag: parseInt(e.target.value, 10) || 21 })} /><p className="text-xs text-slate-500 mt-1">Standard 21 (Abrechnung am 20.). Bestimmt den Zeitraum des Überstunden-Kontos.</p></div></div>
                  <div className="mt-6 flex justify-end gap-3"><button onClick={() => setShowSettingsModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Abbrechen</button><button onClick={saveSettingsHandler} className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-2"><Save className="w-4 h-4" /> Speichern</button></div>
              </div>
          </div>
      )}

      {deleteConfirmState.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-in fade-in zoom-in duration-200 border-l-4 border-red-500">
            <div className="flex items-center gap-3 mb-4 text-red-900"><div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="w-6 h-6 text-red-600" /></div><h3 className="text-lg font-bold">Mitarbeiter löschen?</h3></div><p className="text-slate-600 mb-6 leading-relaxed">Bist du sicher? Alle erfassten Zeiten dieses Mitarbeiters werden unwiderruflich gelöscht.</p>
            <div className="flex justify-end gap-3"><button onClick={() => setDeleteConfirmState({isOpen: false, empId: null})} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors">Abbrechen</button><button onClick={confirmDeleteEmployee} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md transition-colors">Ja, löschen</button></div>
          </div>
        </div>
      )}

      {showPayslipsUserModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-lg w-full max-h-[85vh] flex flex-col">
                  <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                      <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                              <Folder className="w-6 h-6 text-blue-600" />
                          </div>
                          <h3 className="text-xl font-bold text-slate-800">Meine Dokumente</h3>
                      </div>
                      <button onClick={() => setShowPayslipsUserModal(false)} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"><X className="w-5 h-5 text-slate-600" /></button>
                  </div>
                  
                  <div className="overflow-y-auto flex-1 pr-2 space-y-3 allow-scroll">
                      {activePayslips.length > 0 ? (
                          activePayslips.map(p => (
                              <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-200 shadow-sm gap-4">
                                  <div className="flex items-center gap-4">
                                      <div className="p-3 bg-red-100 rounded-xl shrink-0">
                                          <FileText className="w-6 h-6 text-red-600" />
                                      </div>
                                      <div className="overflow-hidden">
                                          <p className="text-base font-bold text-slate-800">{formatMonthName(p.month)}</p>
                                          <p className="text-xs text-slate-500 truncate w-full sm:w-48">{p.fileName}</p>
                                      </div>
                                  </div>
                                  <button onClick={() => downloadBase64PDF(p.fileData, p.fileName)} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors shadow-md w-full sm:w-auto">
                                      <Download className="w-4 h-4" /> Laden
                                  </button>
                              </div>
                          ))
                      ) : (
                          <div className="text-center py-12">
                              <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                              <h4 className="text-lg font-bold text-slate-700 mb-1">Keine Dokumente</h4>
                              <p className="text-slate-500 text-sm">Es sind noch keine Lohnabrechnungen für dich hinterlegt.</p>
                          </div>
                      )}
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}
